/**
 * Thin REST API wrapper для partner-webapp.
 *
 * Auth: при первом обращении делаем POST /api/partner/auth/telegram-webapp с
 * window.Telegram.WebApp.initData → получаем JWT → используем в Authorization
 * для всех последующих запросов. Если бэк недоступен и `VITE_USE_MOCK_FALLBACK=true`
 * (fail-closed: включается ТОЛЬКО явным '=true') — autoFallback к mock.ts.
 *
 * На prod НЕ выставлять `VITE_USE_MOCK_FALLBACK=true` — иначе любой 401/403/5xx
 * подменяется на мок и юзер видит фейковые данные. Любое не-'true' значение = выкл.
 */

import { getWebApp } from './telegram';
import type {
  Partner,
  PartnerBot,
  PartnerHome,
  BasePlan,
  PartnerSubscription,
  PartnerSubscriptionCounts,
  PartnerSubscriptionDetail,
  PartnerSubscriptionsPage,
  PartnerSubscriptionsQuery,
  PartnerClientCounts,
  PartnerClientsPage,
  PartnerClientsQuery,
  WalletTransaction,
  ReferralStats,
  ReferralEntry,
  TopReferrer,
  ClientWithdrawal,
  ClientWithdrawalCounts,
  ClientWithdrawalMethod,
  ClientWithdrawalStatus,
  ClientWithdrawalsPage,
  ClientWithdrawalsQuery,
  TrialSettings,
  WebhookEvent,
  RequiredChannelsState,
  StarsShop,
  Journey,
  WarmupRule,
  WarmupRuleInput,
  PartnerAccountRef,
  TeamAccess,
  TeamActivityPage,
  TeamCatalog,
  TeamInviteResult,
  TeamRole,
  TeamState,
  HoldBot,
} from '@/api/types';

type BackendTrialSettings = {
  enabled: boolean;
  plan_available: boolean;
  days: number;
  devices: number;
  lifetime_enabled?: boolean;
  lifetime_plan_available?: boolean;
  lifetime_days?: number;
  lifetime_devices?: number;
  claimed_total: number;
  whitelist_auto?: boolean;
  whitelist_left?: number;
};

const toTrialSettings = (r: BackendTrialSettings): TrialSettings => ({
  enabled: Boolean(r.enabled),
  planAvailable: Boolean(r.plan_available),
  days: r.days ?? 0,
  devices: r.devices ?? 0,
  lifetimeEnabled: Boolean(r.lifetime_enabled),
  lifetimePlanAvailable: Boolean(r.lifetime_plan_available),
  lifetimeDays: r.lifetime_days ?? 0,
  lifetimeDevices: r.lifetime_devices ?? 0,
  claimedTotal: r.claimed_total ?? 0,
  whitelistAuto: Boolean(r.whitelist_auto),
  whitelistLeft: r.whitelist_left ?? 0,
});

export const API_BASE = import.meta.env.VITE_API_BASE || '';
const USE_MOCK_FALLBACK = import.meta.env.VITE_USE_MOCK_FALLBACK === 'true';

// Preconnect: DNS + TLS handshake к API-домену стартует ПАРАЛЛЕЛЬНО с парсингом
// React. К моменту первого fetch (`api.me()` в main.tsx) соединение уже открыто —
// экономия 100-300ms на cold start через тоннель / мобильную сеть.
if (typeof document !== 'undefined' && API_BASE) {
  try {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = API_BASE;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  } catch {
    // ignore
  }
}

interface AuthState {
  token: string | null;
  partnerId: string | null;
  isOwner: boolean;
  expiresAt: number;
}

let auth: AuthState = { token: null, partnerId: null, isOwner: false, expiresAt: 0 };
let authPromise: Promise<AuthState> | null = null;

class ApiError extends Error {
  /** Сырой технический ярлык (`POST /path → 422`) — для console/Sentry, не для юзера. */
  technical: string;
  constructor(public status: number, public body: unknown, technical: string) {
    // Бэк отдаёт `{error:{code,message}}` — человеческое сообщение кладём в .message,
    // чтобы любой `toast(e.message)` показывал нормальный текст, а не URL+код.
    // FastAPI-валидация (422) отдаёт `{detail:[{loc,msg}]}` — вытаскиваем поле+причину,
    // иначе юзер видел бы голый `POST … → 422` без намёка что не так.
    super(humanizeApiError(body) || technical);
    this.technical = technical;
  }
}

/** Достать человекочитаемую причину из тела ошибки бэка (AppError / FastAPI-валидация). */
function humanizeApiError(body: unknown): string | null {
  const b = body as {
    error?: { message?: string };
    detail?: string | { message?: string } | Array<{ loc?: unknown[]; msg?: string }>;
  } | null;
  if (b?.error?.message) return b.error.message; // наш AppError
  const d = b?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]) {
    const field = Array.isArray(d[0].loc) ? String(d[0].loc[d[0].loc.length - 1]) : '';
    const msg = d[0].msg ?? 'некорректное значение';
    return field ? `Поле «${field}»: ${msg}` : msg;
  }
  if (d && typeof d === 'object' && 'message' in d && typeof d.message === 'string') {
    return d.message; // HTTPException(detail={message})
  }
  return null;
}

// 401/403 на auth или защищённом эндпоинте: JWT истёк / партнёр забанен (бэк отдаёт
// 403) / initData невалиден. __root перехватывает и показывает экран повторной
// авторизации вместо фейково-пустого кабинета из mock'а.
//
// `code`/`channelUrl` — для гейта обязательной подписки: бэк отдаёт 403 с
// code `app.subscription_required` и context.channel_url → __root показывает
// экран подписки вместо generic re-auth.
export class AuthError extends Error {
  code?: string;
  channelUrl?: string;
  reason?: string;
  constructor(public status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'AuthError';
    const err = (body as { error?: { code?: string; context?: { channel_url?: string; reason?: string } } } | null)?.error;
    if (err) {
      this.code = err.code;
      this.channelUrl = err.context?.channel_url;
      this.reason = err.context?.reason;
    }
  }
}

/** Текст для делегата, упёршегося в действие владельца. */
export const OWNER_ONLY_MESSAGE = 'Это действие доступно только владельцу кабинета';
/** Отказ по роли: владелец не выдал это право. */
export const ROLE_FORBIDDEN_MESSAGE = 'Владелец кабинета не открыл вам этот раздел';

/**
 * 403 «owner-only action» для делегата (бэк кладёт `context.reason = "delegate"`).
 * Это не «доступ запрещён» и не повод выкидывать из кабинета: человек залогинен и
 * видит всё остальное, ему просто нельзя выводить деньги и менять настройки
 * владельца (заявка #98).
 */
function isDelegateForbidden(body: unknown): boolean {
  const err = (body as { error?: { context?: { reason?: string } } } | null)?.error;
  return err?.context?.reason === 'delegate';
}

/**
 * Текст отказа делегату. Бэк кладёт `context.role`, когда отказала именно роль (а не
 * владельческая ручка): тогда «только владельцу» было бы неправдой, человеку нужно
 * знать, что это закрыл владелец и просить у него.
 */
function delegateForbiddenMessage(body: unknown): string {
  const ctx = (body as { error?: { context?: { role?: string } } } | null)?.error?.context;
  return ctx?.role ? ROLE_FORBIDDEN_MESSAGE : OWNER_ONLY_MESSAGE;
}

/**
 * Telegram не отдал данные для входа: SDK не загрузился либо initData пустая.
 *
 * Это НЕ 401 от бэка — запрос вообще не уходил, на сервере таких случаев не видно.
 * Раньше сюда подставлялся `ApiError(401)` → экран врал «Сессия истекла, перезапустите
 * WebApp», хотя перезапуск не помогает: у партнёра либо оборвалась связь, либо
 * недоступен telegram.org (свой VPN, блокировки). Отдельный тип нужен, чтобы показать
 * честный экран и отправить маячок в error_reports.
 */
export class TelegramUnavailableError extends Error {
  constructor(public reason: 'no_sdk' | 'no_init_data') {
    super(reason === 'no_sdk' ? 'Telegram SDK is not loaded' : 'Telegram initData is empty');
    this.name = 'TelegramUnavailableError';
  }
}

/** Токен, если он уже есть и живой. Без входа: трекер экранов не должен его запускать. */
export function currentToken(): string | null {
  return auth.token && Date.now() < auth.expiresAt - 30_000 ? auth.token : null;
}

async function authenticate(): Promise<AuthState> {
  if (auth.token && Date.now() < auth.expiresAt - 30_000) return auth;
  if (authPromise) return authPromise;

  authPromise = (async () => {
    const tg = getWebApp();
    if (!tg) throw new TelegramUnavailableError('no_sdk');
    const initData = tg.initData ?? '';
    if (!initData) {
      throw new TelegramUnavailableError('no_init_data');
    }
    const res = await fetch(`${API_BASE}/api/partner/auth/telegram-webapp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ init_data: initData }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(res.status, body, `Auth failed: ${res.status}`);
    }
    const data = (await res.json()) as {
      access_token: string;
      expires_in: number;
      partner_id: string;
      is_owner: boolean;
    };
    auth = {
      token: data.access_token,
      partnerId: data.partner_id,
      isOwner: data.is_owner,
      expiresAt: Date.now() + data.expires_in * 1000,
    };
    return auth;
  })();
  try {
    return await authPromise;
  } finally {
    authPromise = null;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { token } = await authenticate();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    throw new ApiError(res.status, errBody, `${method} ${path} → ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Безопасный запрос с fallback'ом-эмпти. Если бэк недоступен (нет VITE_API_BASE или
 * сетевая/HTTP-ошибка) и `VITE_USE_MOCK_FALLBACK !== 'false'` — возвращаем `emptyFallback()`,
 * иначе пробрасываем ошибку выше. На prod `USE_MOCK_FALLBACK=false` → ошибки видны явно.
 */
async function tryRequest<T>(method: string, path: string, emptyFallback: () => T, body?: unknown): Promise<T> {
  // Мутации (POST/PATCH/DELETE/PUT) НИКОГДА не fallback'ят на mock — иначе UI выглядит
  // как «сработало» при упавшем бэке, партнёр думает что создал бот / запросил вывод /
  // отправил рассылку, по факту ничего не произошло. Только GET может молча отдать пустоту.
  //
  // ⚠️ Для мутаций emptyFallback-лямбды — DEAD CODE: их значения никогда не возвращаются,
  // потому что fallbackAllowed=false. Оставлены для совместимости подписи. Если бэк
  // лежит — мутация выбрасывает ApiError, не возвращает фейк.
  const isMutation = method.toUpperCase() !== 'GET';
  const fallbackAllowed = USE_MOCK_FALLBACK && !isMutation;

  if (!API_BASE) {
    if (fallbackAllowed) return emptyFallback();
    throw new ApiError(0, null, 'VITE_API_BASE not configured');
  }
  try {
    return await request<T>(method, path, body);
  } catch (e) {
    // Нет данных от Telegram — пробрасываем как есть: ни mock, ни экран «сессия истекла»
    // тут не к месту, у партнёра проблема со связью, а не с доступом.
    if (e instanceof TelegramUnavailableError) throw e;
    // 401/403 НИКОГДА не маскируем mock'ом (даже на GET) — это бы скрыло истёкший JWT /
    // забаненного партнёра и показало фейково-пустой кабинет как настоящий. Бросаем
    // AuthError всегда → __root показывает экран повторной авторизации.
    if (e instanceof ApiError && e.status === 403 && isDelegateForbidden(e.body)) {
      e.message = delegateForbiddenMessage(e.body);
      throw e;
    }
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      throw new AuthError(e.status, e.message, e.body);
    }
    if (fallbackAllowed) {
      console.warn('[partner-api] GET fallback to empty:', path, e);
      return emptyFallback();
    }
    throw e;
  }
}

/** Сколько скидки покрывает наценка ботов партнёра (заявка #211). Глубже порога
 *  разницу доплачивает партнёр со своего баланса, а без денег код не сработает. */
export interface DiscountCoverage {
  bots: Array<{
    bot_id: string;
    username: string | null;
    /** Б.п. цены, которые наценка покрывает на любом тарифе бота. */
    coverage_bp: number;
    /** То же на самом выгодном тарифе. */
    max_coverage_bp: number;
  }>;
  spendable_display_minor: number;
  display_currency: string;
}

/** Докупка к «Навсегда» по цене ДО наценки партнёра: для него это себестоимость. */
export interface LifetimeAddonPricing {
  kind: 'traffic' | 'devices';
  key: string;
  gb: number | null;
  devices: number | null;
  /** Копейки рубля. Цена клиенту = это × (1 + addon_markup_bp / 10000). */
  platform_price_display_minor: number;
}

/**
 * Раздел «Навсегда» (второй поставщик, FranCenter) в ценах бота. В `plans` этот тариф
 * не входит: сетка периодов и семейства по устройствам к нему не относятся.
 */
export interface LifetimePricingBlock {
  plan_uuid: string;
  plan_name: string;
  devices: number;
  /** Трафик обхода белых списков, не сгорает. Обычные серверы без лимита. */
  traffic_limit_bytes: number | null;
  /** Тумблер раздела у бота. По умолчанию включён. */
  enabled: boolean;
  /** Закупка в копейках: фикс в рублях, от курса не зависит. Пол своей цены. */
  platform_display_minor: number;
  platform_price_minor: number;
  /** Цена клиенту при СОХРАНЁННОМ режиме цен бота, копейки. */
  retail_display_minor: number;
  /** Своя цена в режиме «свои цены». null: действует запас +50 % к закупке. */
  custom_price_display_minor: number | null;
  /** Наценка партнёра на докупки, уже разрешённая: своя или дефолт платформы. */
  addon_markup_bp: number;
  addon_markup_default_bp: number;
  addons: LifetimeAddonPricing[];
}

// ============ Bot texts (кастомизация) ============

/** Настройки автовывода партнёра. Суммы — в копейках рубля (display-валюта). */
export interface AutoPayoutSettings {
  enabled: boolean;
  threshold_display_minor: number | null;
  method: string | null;
  destination: string | null;
  pin_mode: string | null;
  min_interval_hours: number;
  last_run_at: string | null;
  last_error: string | null;
  /** Способы, которыми вывод реально исполнится сам (CryptoBot, TON при включённом флаге). */
  available_methods: string[];
  min_threshold_display_minor: number;
  /** Выше этого порога вывод не исполнится сам и повиснет ручной заявкой у владельца. */
  auto_execution_cap_display_minor: number;
}

export interface ButtonIcon {
  id: string;
  glyph: string;
}

export interface TextEntryDTO {
  key: string;
  group: string;
  kind: 'text' | 'button';
  phase: number;
  editable: boolean;
  system_reason: string | null;
  default_ru: string;
  default_en: string;
  placeholders: string[];
  current_override: string | null;
  max_len: number;
  current_icon: ButtonIcon | null;
  /** Партнёр снял значок совсем: кнопка без иконки, наш дефолт не подставляется.
   *  Отличается от `current_icon === null` — там дефолт как раз рисуется. */
  icon_disabled?: boolean;
  /** Партнёрская подмена значка. Работает и там, где текст менять нельзя. */
  current_emoji: ButtonIcon | null;
  /** Значок нашего дефолта — что видит клиент без подмены. */
  default_emoji: string | null;
  /** false у строк вёрстки (ветка карточки, буллет): подменять там нечего. */
  emoji_editable: boolean;
}

// ============ Прогрев клиентов (триггерные автосообщения) ============

export interface WarmupTrigger {
  key: string;
  label: string;
  emoji: string;
  group: string;
  group_label: string;
  /** Когда сработает. Тайминг партнёр не меняет — только текст и картинку. */
  schedule_hint: string;
  enabled: boolean;
  /** Что уйдёт клиенту сейчас: свой текст партнёра либо наш дефолт. */
  text: string;
  default_text: string;
  is_custom: boolean;
  media_url: string | null;
  /** Переменные, которые нельзя удалять из текста. */
  placeholders: string[];
  max_len: number;
  available: boolean;
  /** Персональная скидка, которую это сообщение даёт клиенту. Пусто — без скидки. */
  offer_percent?: number | null;
}

export interface WarmupCatalog {
  triggers: WarmupTrigger[];
  groups: { key: string; label: string }[];
  /** `{first_name}` и соседи (заявка #206): работают и в автосообщениях. */
  user_vars?: Array<{ name: string; description: string; example: string }>;
}

// ============ Свой VPN (дерево зеркал) ============

export type MirrorRow = {
  bot_id: string;
  username: string;
  title: string;
  /** 1 = магазин завели прямо от вас, 2 = от вашего зеркала, и так далее. */
  level: number;
  created_at: string;
  clients_count: number;
  /** Сколько этот магазин принёс лично вам за всё время, в центах. */
  earned_usd_minor: number;
};

export type MirrorsOverview = {
  enabled: boolean;
  max_depth: number;
  total_earned_usd_minor: number;
  rows: MirrorRow[];
  /** Ваши боты с тумблером кнопки «Свой VPN» в меню каждого. */
  bots: { bot_id: string; username: string; mirrors_enabled: boolean }[];
};

// ============ Розыгрыши ============

export interface GiveawayTask {
  id: string;
  kind: 'channel' | 'invite';
  position: number;
  channel_id: number | null;
  channel_url: string | null;
  channel_name: string | null;
  invite_count: number | null;
}

export interface GiveawayWinner {
  slot: number;
  tg_user_id: number | null;
  tg_username: string | null;
  /** «@name» или «ID 123». */
  display: string;
  /** Выдан, ещё выдаётся (сбой поставщика, повтор идёт сам) или не выдан: тариф
   *  сняли с продажи, деньги за приз вернулись. */
  prize_status: 'issued' | 'pending' | 'refunded';
}

export interface Giveaway {
  id: string;
  bot_id: string;
  title: string;
  description: string | null;
  status: 'draft' | 'active' | 'drawing' | 'finished' | 'cancelled';
  ends_at: string;
  winners_count: number;
  prize_plan_uuid: string | null;
  prize_plan_name: string;
  prize_platform_price_usd_minor: number;
  /** Сколько ещё заморожено под невыданные призы. Возвращается при отмене и в конце. */
  held_usd_minor: number;
  created_at: string;
  launched_at: string | null;
  finished_at: string | null;
  tasks: GiveawayTask[];
  entries_count: number;
  winners_drawn: number;
  /** Кто выиграл: только у завершённого розыгрыша. */
  winners: GiveawayWinner[];
}

export interface GiveawayEscrowPreview {
  per_prize_usd_minor: number;
  escrow_cost_usd_minor: number;
  escrow_cost_display_minor: number;
  /** Сколько вернётся, если победителей стало меньше (только у запущенного). */
  refund_usd_minor: number;
  display_currency: string;
  spendable_usd_minor: number;
  revenue_usd_minor: number;
  paid_usd_minor: number;
  spendable_display_minor: number;
  enough: boolean;
}

export interface GiveawayTaskInput {
  kind: 'channel' | 'invite';
  /** Сырой ввод для channel: @username, ссылка t.me или числовой ID. */
  channel?: string;
  invite_count?: number;
}

// ============ Переезд бота с другой франшизы ============
// Зеркало типов на сайте (partner-site/src/lib/api.ts). Про поставщика здесь нет
// ничего намеренно: партнёр видит только итоговую сумму переноса.

export type BotImportStatus =
  | 'draft'
  | 'awaiting_decision'
  | 'awaiting_payment'
  | 'running'
  | 'paused'
  | 'done'
  | 'cancelled';

export interface BotImport {
  id: string;
  status: BotImportStatus;
  source_name: string | null;
  bot_id: string | null;
  bot_username: string | null;
  /** Ответ партнёра на «переносить подписки?». null, пока не ответил. */
  wants_transfer: boolean | null;
  /** Кто платит за перенос: `free` мы, `paid` партнёр, `none` не переносим. null, пока не решено. */
  payer: 'free' | 'paid' | 'none' | null;
  rows: number;
  clients: number;
  /** Подписки к переносу: ждущие, перенесённые и те, что не перенеслись сразу. */
  subs_total: number;
  subs_done: number;
  /** Не перенеслись сразу: ими занимается платформа. */
  subs_problem: number;
  /** Сумма переноса, пока ждём оплаты. Точная сумма приходит в charge-preview. */
  price_display_minor: number | null;
  paid_display_minor: number | null;
  display_currency: string;
  created_at: string;
  finished_at: string | null;
}

/** Роль колонки выгрузки. `tg_id` обязательна, остальные по желанию. */
export type BotImportRole =
  | 'tg_id'
  | 'username'
  | 'end_date'
  | 'days_left'
  | 'devices'
  | 'plan'
  | 'balance';

/** Разметка колонок: роль → номер колонки с нуля. */
export type BotImportMapping = Partial<Record<BotImportRole, number>>;

export interface BotImportRowsPreview {
  headers: string[];
  has_header: boolean;
  /** Первые строки файла как есть, для таблицы-превью. */
  sample: string[][];
  mapping: BotImportMapping;
  rows_valid: number;
  clients: number;
  with_subscription: number;
  error_count: number;
  /** Первые ошибки с номером строки файла. Полное число в `error_count`. */
  errors: { line: number; message: string }[];
  duplicates: number;
  /** Файл длиннее лимита: прочитано только начало. */
  truncated: boolean;
  committed: boolean;
  /** Подозрительная разметка, но не ошибка строк: например колонка устройств, похожая
   *  на число подключённых устройств, а не на тариф. */
  warnings?: string[];
  /** Переезд после сохранения базы (только при `dry_run=false`). */
  import?: BotImport | null;
}

export interface BotImportChargePreview {
  subs_count: number;
  amount_display_minor: number;
  display_currency: string;
  spendable_display_minor: number;
  enough: boolean;
}

/** Своя кнопка партнёра: либо ссылка, либо переход в наш экран. */
export interface CustomButton {
  text: string;
  url?: string;
  kind?: 'action';
  action?: string;
  /** Стабильный id, выданный сервером: по нему кнопка стоит в раскладке меню
   *  (`custom:<id>`) и по нему же правится. По позиции адресовать нельзя — удалили
   *  соседнюю, и все остальные съехали на чужие места. */
  id?: string;
  /** Значок перед подписью (обычный эмодзи), заявка #176. */
  icon?: string;
  /** Премиум-эмодзи: иконкой кнопки, если у бота включён премиум. Только вместе с `icon`. */
  icon_id?: string;
  /** Цвет кнопки (`primary`/`success`/`danger`), заявка #171. Нет поля — без цвета. */
  style?: string;
}

export interface CustomButtons {
  /** Ключи разделов: menu | profile | subs. */
  sections: Record<string, CustomButton[]>;
  max_per_section: number;
  available_actions: Array<{ key: string; label: string }>;
}

/** Кнопка меню бота в конструкторе. */
export interface MenuSlot {
  key: string;
  /** Готовая подпись с учётом партнёрского override — то же, что видит клиент. */
  label: string;
  text_key: string;
  /** false — кнопку нельзя спрятать (вход в продажи и в продление). */
  removable: boolean;
  /** Условие появления у клиента, если кнопка не постоянная. */
  conditional: string | null;
  /** Своя кнопка партнёра: подпись и адрес правятся на вкладке «Свои». */
  custom?: boolean;
  /** Вторая подпись у кнопки-переключателя («Заморозить» ↔ «Разморозить»). */
  alt_text_key?: string | null;
  alt_label?: string | null;
  /** Когда показывается вторая подпись — словами. */
  alt_hint?: string | null;
  /** Цвет, выбранный партнёром (`primary`/`success`/`danger`/`none`), заявка #171.
   *  null — наш цвет по умолчанию. */
  style?: string | null;
  /** Наш цвет без выбора партнёра: акцент `primary` у «Пробника» и «Купить». */
  default_style?: string | null;
  /** Экран мини-аппа, который открывает кнопка (заявка #181). null — кнопка в чате. */
  webapp_target?: string | null;
  /** Экран, который предлагаем первым. null — кнопку в мини-апп не перевести. */
  webapp_default?: string | null;
  /** Что теряет клиент, если кнопка открывается в мини-аппе. */
  webapp_note?: string | null;
}

export interface MenuLayoutSection {
  /** Название раздела для кабинета. */
  title: string;
  rows: string[][];
  hidden: string[];
  slots: MenuSlot[];
  /** Ключ сообщения, которое бот шлёт над этими кнопками. */
  text_key: string;
  /** Секция баннера этого экрана в `brand.banners`. Пусто — картинки у экрана нет. */
  banner_section: string;
  /** Что сейчас показывается: своя картинка секции либо унаследованная главная. */
  banner_url: string | null;
  /** Картинка пришла от главного экрана, своей у раздела нет. */
  banner_inherited: boolean;
  /** Партнёр снял картинку с этого экрана — это не то же, что «не задано». */
  banner_disabled: boolean;
  /** Что про раздел нужно знать партнёру. */
  note?: string | null;
  /** Можно ли добавить сюда свою кнопку. */
  custom_allowed: boolean;
}

export interface MenuLayoutResponse {
  sections: Record<string, MenuLayoutSection>;
  max_per_row: number;
  /** Экраны мини-аппа, которые может открыть встроенная кнопка (заявка #181). */
  webapp_targets?: Array<{ key: string; label: string }>;
  /** Мини-апп у бота включён и доступен по https. Нет — кнопки остаются в чате. */
  webapp_available?: boolean;
}

export interface BroadcastButtonResult {
  index: number;
  row: number;
  col: number;
  text: string;
  /** `section` — раздел бота, `webapp` — экран мини-аппа, `link` — ссылка. */
  kind: 'section' | 'webapp' | 'link';
  target: string;
  counted: boolean;
  clicks: number;
}

export interface BroadcastResults {
  tracked: boolean;
  delivered: number;
  failed: number;
  blocked: number;
  buttons: BroadcastButtonResult[];
  buyers: number;
  purchases: number;
  revenue_usd_minor: number;
  margin_usd_minor: number;
  window_ends_at: string | null;
  window_open: boolean;
  fx_rate_to_display: number;
  display_currency: string;
}

export interface TextCatalog {
  entries: TextEntryDTO[];
  groups: string[];
  /** Мастер-тумблер бота: без него премиум-иконки кнопок в Telegram не уходят. */
  premium_emoji_enabled: boolean;
  /** `{first_name}` и соседи: работают в любом тексте бота (заявка #206). */
  user_vars?: Array<{ name: string; description: string; example: string }>;
  /** Слово вместо имени, когда Telegram его не отдал. */
  name_fallback?: string;
  /** Примеры значений переменных для превью (В1). */
  samples?: Record<string, string>;
  key_samples?: Record<string, Record<string, string>>;
  /** Цвет иконок бота (ключ из `ICON_COLORS`) или null. */
  icon_color?: string | null;
}

/** Иконка каталога: `svg` красится CSS-цветом, `colors` это цвета, в которых есть своя версия. */
export interface EmojiIcon {
  id: string;
  glyph: string;
  svg: string;
  colors: string[];
}

export interface EmojiIcons {
  icons: EmojiIcon[];
  palette: { key: string; label: string; hex: string; icons_ready: number; ready: boolean }[];
}

/** Ответ `PATCH /icon-color`: нужен только новый цвет. */
export interface IconColorResponse {
  id: string;
  icon_color: string | null;
}

// ============ Mini-app texts (кастомизация client-webapp) ============

export interface WebappTextEntry {
  key: string;
  group: string;
  default_ru: string;
  default_en: string;
  current_override: string | null;
  max_len: number;
}

export interface WebappTextCatalog {
  entries: WebappTextEntry[];
  groups: string[];
}

// ============ Отзывы ============

export type ReviewStatusFilter = 'all' | 'published' | 'hidden';

/** Партнёр видит отзыв целиком, с настоящим ником: ему решать, что с ним делать. */
export interface PartnerReview {
  id: string;
  rating: number;
  text: string | null;
  author_label: string;
  status: 'published' | 'hidden';
  /** `partner` — отзыв заведён руками, значка подтверждённой подписки у него нет. */
  source: 'client' | 'partner';
  positive: boolean;
  created_at: string;
  months_with_us: number | null;
  subject_title: string | null;
  hidden_at: string | null;
  /** Ответ партнёра под отзывом (заявка #104). Один на отзыв, правится на месте. */
  reply_text: string | null;
  replied_at: string | null;
}

export interface PartnerReviewsPage {
  items: PartnerReview[];
  next_cursor: string | null;
  has_more: boolean;
  /** Счётчики по всей базе бота, а не по текущей странице. */
  counts: { published: number; hidden: number; total: number };
}

export interface ReviewsConfig {
  enabled: boolean;
  glyph: string;
  verified_badge: boolean;
  /** Через сколько дней после оплаты просить отзыв. 0 — не просить. */
  ask_after_days: number;
  promo: { enabled: boolean; title: string; subtitle: string };
  /** Скидка на следующую покупку в благодарность за отзыв. Платит партнёр. */
  reward: { enabled: boolean; percent: number };
}

export interface ManualReviewInput {
  author_label: string;
  rating: number;
  text?: string | null;
  subject_title?: string | null;
  months_with_us?: number | null;
  created_at?: string | null;
}

// ============ UTM-кампании ============

export interface CampaignItem {
  id: string;
  name: string;
  /** Заметка партнёра (#228), видит только он. */
  note?: string | null;
  code: string;
  status: 'active' | 'archived';
  bot_id: string | null;
  bot_username: string | null;
  link: string | null;
  created_at: string;
  starts: number;
  buyers: number;
  purchases: number;
  trials: number;
  paid_purchases: number;
  revenue_minor: number; // USD-cents
  margin_minor: number; // USD-cents
  spend_by_currency: Partial<Record<'RUB' | 'USD', number>>;
  spend_usd_minor: number;
  roi_percent: number | null;
  conversion_percent: number;
  /** Ссылка на витрину бота с меткой кампании. null — сайт не работает. */
  site_link?: string | null;
  /** Из `starts`: зарегистрировались на сайте по ссылке. */
  site_starts?: number;
  /** Заходы на сайт по ссылке: уникальные посетители за сутки. */
  site_visits?: number;
}

export interface CampaignListResponse {
  items: CampaignItem[];
  fx_rate_to_display: number;
  display_currency: string;
}

export interface CampaignSpendEntry {
  id: string;
  spent_on: string; // YYYY-MM-DD
  amount_minor: number;
  currency: 'RUB' | 'USD';
  comment: string | null;
}

export interface CampaignDayBucket {
  day: string;
  starts: number;
  purchases: number;
  revenue_minor: number;
  margin_minor: number;
}

export interface CampaignStatsResponse {
  campaign: CampaignItem;
  daily: CampaignDayBucket[];
  plans: Array<{ plan_uuid: string; plan_name: string; sales_count: number; revenue_minor: number }>;
  spend_entries: CampaignSpendEntry[];
  fx_rate_to_display: number;
  display_currency: string;
}

export interface UtmTrafficRow {
  source: string;
  campaign: string | null;
  medium: string | null;
  starts: number;
  buyers: number;
  revenue_minor: number;
  margin_minor: number;
}

export interface UtmTrafficResponse {
  items: UtmTrafficRow[];
  fx_rate_to_display: number;
  display_currency: string;
}

const EMPTY_CAMPAIGN: CampaignItem = {
  id: '',
  name: '',
  code: '',
  status: 'active',
  bot_id: null,
  bot_username: null,
  link: null,
  created_at: '',
  starts: 0,
  buyers: 0,
  purchases: 0,
  trials: 0,
  paid_purchases: 0,
  revenue_minor: 0,
  margin_minor: 0,
  spend_by_currency: {},
  spend_usd_minor: 0,
  roi_percent: null,
  conversion_percent: 0,
};

/**
 * FX-курс USD→display, мемоизированный на время сессии.
 *
 * Подписки грузятся страницами, и тянуть /api/partner/me на каждую догрузку незачем:
 * курс обновляется по CBR раз в сутки, а сессия мини-аппа живёт минуты.
 */
let fxRatePromise: Promise<number> | null = null;
function usdToDisplayRate(): Promise<number> {
  fxRatePromise ??= tryRequest<{ usd_to_display_rate?: number }>(
    'GET',
    '/api/partner/me',
    () => ({ usd_to_display_rate: 100 }),
  )
    .then((m) => m.usd_to_display_rate || 100)
    .catch(() => {
      fxRatePromise = null; // не залипаем на неудачной попытке
      return 100;
    });
  return fxRatePromise;
}

/** Заявка клиента на вывод в том виде, в каком её отдаёт бэк. */
type BackendClientWithdrawal = {
  id: string;
  status: string;
  created_at: string;
  taken_at?: string | null;
  decided_at: string | null;
  amount_display_minor: number;
  display_currency: string;
  amount_usd_minor: number;
  method: string;
  destination: string;
  client_id: string;
  client_label: string;
  bot_id: string | null;
  bot_username: string | null;
  reject_reason: string | null;
  invited: number;
  paying: number;
  earned_usd_minor: number;
  memo?: string | null;
  payout_mode?: string;
  net_usd_minor?: number | null;
  net_display_minor?: number | null;
  owner_approval?: string | null;
  transfer_state?: string | null;
  tx_hash?: string | null;
  platform_available?: boolean;
};

const toClientWithdrawal = (w: BackendClientWithdrawal, rate: number): ClientWithdrawal => ({
  id: w.id,
  status: w.status as ClientWithdrawalStatus,
  createdAt: w.created_at,
  takenAt: w.taken_at ?? null,
  decidedAt: w.decided_at,
  amountDisplayMinor: w.amount_display_minor,
  displayCurrency: w.display_currency,
  amountUsdMinor: w.amount_usd_minor,
  method: w.method as ClientWithdrawalMethod,
  destination: w.destination,
  clientId: w.client_id,
  clientLabel: w.client_label,
  botId: w.bot_id,
  botUsername: w.bot_username,
  rejectReason: w.reject_reason,
  invited: w.invited ?? 0,
  paying: w.paying ?? 0,
  earnedRub: Math.round(((w.earned_usd_minor ?? 0) * rate) / 100),
  memo: w.memo ?? null,
  payoutMode: w.payout_mode === 'platform' ? 'platform' : 'manual',
  netUsdMinor: w.net_usd_minor ?? null,
  netDisplayMinor: w.net_display_minor ?? null,
  ownerApproval:
    w.owner_approval === 'required' || w.owner_approval === 'approved' ? w.owner_approval : null,
  transferState: w.transfer_state ?? null,
  txHash: w.tx_hash ?? null,
  platformAvailable: Boolean(w.platform_available),
});

// --- Статус сети и мониторинг сервисов (/status) ---------------------------
export interface StatusUptimeDay {
  day: string;
  pct: number;
  status: string;
}
export interface StatusComponent {
  id: string;
  label: string;
  status: string;
  latency_ms: number | null;
  uptime_pct_90d: number | null;
  days: StatusUptimeDay[];
}
export interface StatusServer {
  remark: string | null;
  up: boolean | null;
  latency_ms: number | null;
}
export interface StatusCountry {
  flag: string;
  country_ru: string;
  /** ISO-код («DE») с сервера: по нему картинка флага и имя страны на английском. */
  country_code?: string | null;
  up: number;
  total: number;
  servers: StatusServer[];
}
export interface StatusNetwork {
  countries: number;
  servers: number;
  nodes_up: number;
  nodes_total: number;
  synced_at: string | null;
  checked_at: string | null;
  regions: Record<string, StatusCountry[]>;
}
export interface NetworkStatusResponse {
  overall: string;
  updated_at: string | null;
  /** Порядок и подписи задаёт бэк. С «Навсегда» добавляется `vpn_network_lifetime`. */
  components: StatusComponent[];
  /** Сеть раздела «На срок» (AdaptGroup). */
  network: StatusNetwork;
  /** Сеть раздела «Навсегда» (FranCenter). Null, пока раздел не работает. */
  lifetime_network?: StatusNetwork | null;
}

// ============ Auth ============


// ---- VPN Reseller API: ключ и вебхуки (кабинет партнёра) ----

export interface VpnApiKeyInfo {
  has_key: boolean;
  key_prefix: string | null;
  created_at: string | null;
  last_used_at: string | null;
  api_enabled: boolean;
}

export interface VpnApiKeyIssued {
  api_key: string;
  key_prefix: string;
  created_at: string;
}

export interface VpnApiWebhookInfo {
  url: string | null;
  events: string[] | null;
  is_active: boolean;
  secret_set: boolean;
  all_events: string[];
  last_success_at: string | null;
  last_failure_at: string | null;
  consecutive_failures: number;
}

export interface VpnApiWebhookTest {
  delivered: boolean;
  status: string;
  http_status: number | null;
  error: string | null;
  duration_ms: number;
  delivery_id: string;
}

export interface VpnApiStats {
  period: string;
  deposit: {
    spendable_minor: number;
    spendable_usd: string;
    paid_minor: number;
    paid_usd: string;
    earned_minor: number;
    earned_usd: string;
    /** null — выдач ещё не было, средней цены нет и обещать число нельзя. */
    affordable_subscriptions: number | null;
  };
  spent_minor: number;
  spent_usd: string;
  operations: {
    total: number;
    done: number;
    failed: number;
    failure_rate_bp: number;
    by_operation: { operation: string; count: number; done: number; spent_minor: number }[];
    by_error: { error_code: string; count: number }[];
  };
  subscriptions: { created: number; renewed: number; active: number; expiring_soon: number };
  daily: { date: string; spent_minor: number; created: number }[];
}

export interface VpnApiOperation {
  custom_id: string;
  operation: string;
  status: string;
  http_status: number | null;
  error_code: string | null;
  charged_minor?: number;
  charged_usd?: string;
  subscription_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface VpnApiDelivery {
  delivery_id: string;
  event: string;
  status: string;
  attempts: number;
  http_status: number | null;
  error: string | null;
  duration_ms: number | null;
  is_manual: boolean;
  created_at: string | null;
  delivered_at: string | null;
  next_attempt_at: string | null;
}

/**
 * Какую языковую версию текстов бота правит редактор (#274). Одна на мини-апп: её
 * берут каталог и сохранение разом. Вне редактора всегда `ru`.
 */
let textsLocale: 'ru' | 'en' = 'ru';
export function setTextsLocale(locale: 'ru' | 'en'): void {
  textsLocale = locale;
}
export function getTextsLocale(): 'ru' | 'en' {
  return textsLocale;
}

export const api = {
  // VPN Reseller API
  vpnApiKey: () => request<VpnApiKeyInfo>('GET', '/api/partner/vpn-api/key'),
  reissueVpnApiKey: () => request<VpnApiKeyIssued>('POST', '/api/partner/vpn-api/key/reissue'),
  revokeVpnApiKey: () => request<VpnApiKeyInfo>('DELETE', '/api/partner/vpn-api/key'),
  vpnApiWebhook: () => request<VpnApiWebhookInfo>('GET', '/api/partner/vpn-api/webhook'),
  saveVpnApiWebhook: (body: { url: string | null; events?: string[] | null }) =>
    request<VpnApiWebhookInfo>('PUT', '/api/partner/vpn-api/webhook', body),
  vpnApiWebhookSecret: () => request<{ secret: string }>('GET', '/api/partner/vpn-api/webhook/secret'),
  reissueVpnApiWebhookSecret: () =>
    request<{ secret: string }>('POST', '/api/partner/vpn-api/webhook/secret/reissue'),
  testVpnApiWebhook: () => request<VpnApiWebhookTest>('POST', '/api/partner/vpn-api/webhook/test'),
  vpnApiStats: (period: 'day' | 'week' | 'month' | 'all' = 'month') =>
    request<VpnApiStats>('GET', `/api/partner/vpn-api/stats?period=${period}`),
  vpnApiOperations: (params: { limit?: number; offset?: number; status?: string } = {}) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(params.limit ?? 20));
    qs.set('offset', String(params.offset ?? 0));
    if (params.status) qs.set('status', params.status);
    return request<{ total: number; offset: number; limit: number; items: VpnApiOperation[] }>(
      'GET',
      `/api/partner/vpn-api/operations?${qs.toString()}`,
    );
  },
  vpnApiWebhookDeliveries: (limit = 30) =>
    request<{ items: VpnApiDelivery[] }>('GET', `/api/partner/vpn-api/webhook/deliveries?limit=${limit}`),
  // Authentication state (after first call)
  getAuth: () => auth,

  // GET /api/partner/status — те же данные, что на странице статуса сайта.
  networkStatus: () => request<NetworkStatusResponse>('GET', '/api/partner/status'),

  // GET /api/partner/me. Бэк отдаёт snake_case — мапим в camelCase UI-форму Partner.
  // Поля, которых нет в бэке (`balanceRub`, `totalEarnedRub`, `tgId`, `username`,
  // `language`) — нулевые/null дефолты; `tgId` берётся в UI из getTgUser().
  me: async (): Promise<Partner> => {
    type BackendMe = {
      id: string;
      display_name: string;
      is_owner: boolean;
      is_active: boolean;
      has_email: boolean;
      has_tg: boolean;
      created_at: string;
      franchise_bot_username?: string | null;
      // Phase 4 follow-up — реальные значения вместо нулей.
      balance_usd_minor: number;
      paid_usd_minor?: number;
      spendable_usd_minor?: number;
      held_usd_minor?: number;
      hold_bots?: HoldBot[];
      total_earned_usd_minor: number;
      usd_to_display_rate: number;
      is_delegate?: boolean;
      delegate_role?: TeamRole | null;
      delegate_permissions?: string[] | null;
      accounts?: PartnerAccountRef[];
    };
    const raw = await tryRequest<BackendMe>('GET', '/api/partner/me', () => ({
      id: 'partner_local',
      display_name: '',
      is_owner: false,
      is_active: true,
      has_email: false,
      has_tg: true,
      created_at: new Date().toISOString(),
      franchise_bot_username: null,
      balance_usd_minor: 0,
      paid_usd_minor: 0,
      spendable_usd_minor: 0,
      total_earned_usd_minor: 0,
      usd_to_display_rate: 100,
      is_delegate: false,
      accounts: [],
    }));
    // cents × rate / 100 = display-валюта (RUB по дефолту).
    const fx = raw.usd_to_display_rate || 100;
    return {
      id: raw.id,
      tgId: 0,
      username: null,
      firstName: raw.display_name,
      status: raw.is_active ? 'active' : 'banned',
      createdAt: raw.created_at,
      balanceRub: Math.round((raw.balance_usd_minor * fx) / 100),
      paidRub: Math.round(((raw.paid_usd_minor ?? 0) * fx) / 100),
      // Бэк отдаёт spendable сам; `??` держит старый ответ, пока API не обновлён.
      spendableRub: Math.round(
        ((raw.spendable_usd_minor ?? raw.balance_usd_minor + (raw.paid_usd_minor ?? 0)) * fx) / 100,
      ),
      heldRub: Math.round(((raw.held_usd_minor ?? 0) * fx) / 100),
      holdBots: raw.hold_bots ?? [],
      totalEarnedRub: Math.round((raw.total_earned_usd_minor * fx) / 100),
      language: 'ru',
      franchiseBotUsername: raw.franchise_bot_username ?? null,
      isDelegate: raw.is_delegate === true,
      delegateRole: raw.delegate_role ?? null,
      delegatePermissions: raw.delegate_permissions ?? null,
      accounts: raw.accounts ?? [],
    };
  },

  // ---- Команда партнёра ----
  team: () => request<TeamState>('GET', '/api/partner/team'),
  /** Каталог прав для экрана галочек: тот же список, по которому бэк проверяет доступ. */
  teamPermissions: () => request<TeamCatalog>('GET', '/api/partner/team/permissions'),
  /** Приглашение с ролью и правами. `handle` (@ник или ID) пробует доставить его в личку. */
  createTeamInvite: (body?: TeamAccess & { handle?: string | null }) =>
    request<TeamInviteResult>('POST', '/api/partner/team/invites', body),
  /** Журнал: что менял кто из команды. Курсор из прошлой страницы, `adminId` сужает до человека. */
  teamActivity: (params: { adminId?: string | null; cursor?: string | null; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.adminId) q.set('admin_id', params.adminId);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return request<TeamActivityPage>('GET', `/api/partner/team/activity${qs ? `?${qs}` : ''}`);
  },
  /** Сменить роль и права. Действует со следующего запроса человека. */
  setTeamAccess: (partnerId: string, body: TeamAccess) =>
    request<{ partner_id: string; role: TeamRole; permissions: string[] }>(
      'PUT',
      `/api/partner/team/${partnerId}/access`,
      body,
    ),
  cancelTeamInvite: (token: string) =>
    request<void>('DELETE', `/api/partner/team/invites/${encodeURIComponent(token)}`),
  revokeTeamMember: (partnerId: string) =>
    request<void>('DELETE', `/api/partner/team/${partnerId}`),
  /**
   * Переключить кабинет. Токен подменяем в том же синглтоне сессии: заново
   * авторизоваться по initData не нужно, личность не менялась.
   */
  switchContext: async (partnerId: string) => {
    const r = await request<{
      access_token: string;
      expires_in: number;
      partner_id: string;
      is_delegate: boolean;
    }>('POST', '/api/partner/context/switch', { partner_id: partnerId });
    auth = {
      ...auth,
      token: r.access_token,
      expiresAt: Date.now() + r.expires_in * 1000,
    };
    return r;
  },

  // GET /api/partner/dashboard/stats?period_days=N. Backend → {active_subscriptions,
  // new_subscriptions, expired_subscriptions, revenue_minor (USD-cents), top_plans[],
  // revenue_14d[]}. Маппим в плоскую UI-shape, считаем display-валюту.
  // GET /api/partner/home — то, что требует внимания партнёра (бот на паузе, продажа по
  // себестоимости, отзывы без ответа, выводы...), тот же ответ, что у сводки на сайте.
  // Ссылки собирает экран: у мини-аппа свои адреса.
  partnerHome: (botId: string | null = null) =>
    tryRequest<PartnerHome>(
      'GET',
      `/api/partner/home${botId ? `?bot_id=${encodeURIComponent(botId)}` : ''}`,
      () => ({ checklist: [], trial_enabled: false, attention: [] }),
    ),

  /** Автосписания у кассы: живые, выручка в месяц и события за период. */
  recurrentStats: (periodDays: number = 30, botId: string | null = null) => {
    const qs = new URLSearchParams({ period_days: String(periodDays) });
    if (botId) qs.set('bot_id', botId);
    return request<RecurrentStats>('GET', `/api/partner/dashboard/recurrents?${qs.toString()}`);
  },

  dashboardStats: async (periodDays: number = 30, botId: string | null = null): Promise<{
    active_subs: number;
    new_subs: number;
    revenue_rub: number;
    margin_rub: number;
    avg_check_rub: number;
    churn_30d_percent: number;
    // «Пришло» за период + когортная конверсия (купило / пришло, %).
    new_clients: number;
    converted_clients: number;
    conversion_percent: number;
    top_plans: Array<{ plan_id: string; plan_name?: string; count: number }>;
    mrr_14d: Array<{ day: string; mrr: number; margin: number; subs: number }>;
    // Donut-разбивка выручки по ботам (пусто при фильтре на один бот).
    bot_breakdown: Array<{ bot_id: string; label: string; revenue_rub: number; sales: number }>;
    margin_minor?: number;
    // Сколько роздано скидками за период. Прибыль уже посчитана за их вычетом, и без
    // этой строки она читается как недоплата (заявка #92).
    discount_rub: number;
    // Продлений, апгрейдов и докупок трафика за период. Раньше их не было в
    // статистике вовсе, хотя это около 11.6% заработка.
    repeat_sales: number;
    // Из чего сложилась прибыль, ₽. Сумма четырёх равна margin_rub.
    sales_rub: number;
    mirror_bonus_rub: number;
    referral_paid_rub: number;
    other_rub: number;
    // % изменения выручки vs предыдущий период такой же длины. null = не с чем
    // сравнивать (предыдущий период пустой и текущий тоже).
    revenue_change_percent: number | null;
    // То же для прибыли (маржи партнёра). На главной показывается именно она, и
    // пилюля тренда рядом обязана считаться от неё же, а не от оборота.
    margin_change_percent: number | null;
    fx_rate: number;
  }> => {
    type BackendStats = {
      period_days: number;
      active_subscriptions: number;
      new_subscriptions: number;
      expired_subscriptions: number;
      revenue_minor: number;
      currency: string;
      avg_check_minor: number;
      new_clients?: number;
      converted_clients?: number;
      bot_breakdown?: Array<{ bot_id: string; bot_label: string; revenue_minor: number; sales_count: number }>;
      top_plans: Array<{
        plan_uuid: string;
        plan_name: string;
        sales_count: number;
        revenue_minor: number;
      }>;
      margin_minor: number;
      discount_minor?: number;
      repeat_sales?: number;
      sales_minor?: number;
      mirror_bonus_minor?: number;
      referral_paid_minor?: number;
      other_minor?: number;
      revenue_14d: Array<{ day: string; revenue_minor: number; margin_minor: number; sales_count?: number }>;
      fx_rate_to_display?: number;
      display_currency?: string;
      revenue_prev_minor?: number;
      margin_prev_minor?: number;
    };
    const qs = new URLSearchParams({ period_days: String(periodDays) });
    if (botId) qs.set('bot_id', botId);
    const raw = await tryRequest<BackendStats>('GET', `/api/partner/dashboard/stats?${qs.toString()}`, () => ({
      period_days: 30,
      active_subscriptions: 0,
      new_subscriptions: 0,
      expired_subscriptions: 0,
      revenue_minor: 0,
      currency: 'USD',
      avg_check_minor: 0,
      new_clients: 0,
      converted_clients: 0,
      bot_breakdown: [],
      top_plans: [],
      margin_minor: 0,
      revenue_14d: [],
    }));
    // Живой FX-курс из бэка (CBR, обновляется ежечасно — get_display_rate).
    // Фолбэк 100 — только если бэк не вернул rate (консистентно с backend
    // settings.pricing.usd_to_display_rate). Никаких хардкод-курсов в проде.
    const fxRate = raw.fx_rate_to_display && raw.fx_rate_to_display > 0 ? raw.fx_rate_to_display : 100;
    const revenueRub = Math.round((raw.revenue_minor * fxRate) / 100);
    const avgCheckRub = Math.round((raw.avg_check_minor * fxRate) / 100);
    // Тренд выручки vs предыдущий период. % валюто-агностичен (отношение minor-сумм).
    // Если предыдущий период пустой: новый рост со старта показываем +100%, а если
    // и текущий ноль — null (нечего показывать).
    const revPrev = raw.revenue_prev_minor ?? 0;
    const revenueChangePercent =
      revPrev > 0
        ? Math.round(((raw.revenue_minor - revPrev) / revPrev) * 100)
        : raw.revenue_minor > 0
          ? 100
          : null;
    const churn = raw.active_subscriptions + raw.expired_subscriptions === 0
      ? 0
      : (raw.expired_subscriptions / (raw.active_subscriptions + raw.expired_subscriptions)) * 100;
    const marginRub = Math.round((raw.margin_minor * fxRate) / 100);
    const marginPrev = raw.margin_prev_minor ?? 0;
    const marginChangePercent =
      marginPrev > 0
        ? Math.round(((raw.margin_minor - marginPrev) / marginPrev) * 100)
        : raw.margin_minor > 0
          ? 100
          : null;
    const newClients = raw.new_clients ?? 0;
    const convertedClients = raw.converted_clients ?? 0;
    // Когортная конверсия пришло→купило, %. 0 если за период никто не пришёл.
    const conversionPercent = newClients > 0 ? Math.round((convertedClients / newClients) * 100) : 0;
    return {
      active_subs: raw.active_subscriptions,
      new_subs: raw.new_subscriptions,
      revenue_rub: revenueRub,
      margin_rub: marginRub,
      avg_check_rub: avgCheckRub,
      churn_30d_percent: churn,
      new_clients: newClients,
      converted_clients: convertedClients,
      conversion_percent: conversionPercent,
      top_plans: raw.top_plans.map((p) => ({
        plan_id: p.plan_uuid,
        plan_name: p.plan_name,
        count: p.sales_count,
      })),
      mrr_14d: raw.revenue_14d.map((b) => ({
        day: b.day,
        mrr: Math.round((b.revenue_minor * fxRate) / 100),
        margin: Math.round((b.margin_minor * fxRate) / 100),
        subs: b.sales_count ?? 0,
      })),
      bot_breakdown: (raw.bot_breakdown ?? []).map((b) => ({
        bot_id: b.bot_id,
        label: b.bot_label,
        revenue_rub: Math.round((b.revenue_minor * fxRate) / 100),
        sales: b.sales_count,
      })),
      margin_minor: raw.margin_minor,
      discount_rub: Math.round(((raw.discount_minor ?? 0) * fxRate) / 100),
      repeat_sales: raw.repeat_sales ?? 0,
      sales_rub: Math.round(((raw.sales_minor ?? 0) * fxRate) / 100),
      mirror_bonus_rub: Math.round(((raw.mirror_bonus_minor ?? 0) * fxRate) / 100),
      referral_paid_rub: Math.round(((raw.referral_paid_minor ?? 0) * fxRate) / 100),
      other_rub: Math.round(((raw.other_minor ?? 0) * fxRate) / 100),
      revenue_change_percent: revenueChangePercent,
      margin_change_percent: marginChangePercent,
      fx_rate: fxRate,
    };
  },

  // GET /api/partner/bots. Backend → BotResponse {id, tg_bot_id, username, title,
  // is_active, created_at}. UI ждёт PartnerBot {id, username, brandName, status,
  // activeSubs, mrrRub, createdAt}. activeSubs/mrrRub нет в списке — нули; для деталей
  // используется `botStats()`.
  bots: async (): Promise<PartnerBot[]> => {
    type BackendBot = {
      id: string;
      tg_bot_id: number;
      username: string;
      title: string;
      is_active: boolean;
      created_at: string;
      // Phase 4 follow-up: per-bot агрегаты теперь в list endpoint.
      active_subs: number;
      total_users: number;
      new_users_today: number;
      /** Доход партнёра в месяц по подпискам бота (USD-cents). */
      monthly_income_usd_minor?: number;
      site_enabled?: boolean;
      site_url?: string | null;
    };
    const [rows, rate] = await Promise.all([
      tryRequest<BackendBot[]>('GET', '/api/partner/bots', () => []),
      usdToDisplayRate(),
    ]);
    return rows.map((b) => ({
      id: b.id,
      username: b.username,
      brandName: b.title,
      status: b.is_active ? 'active' : 'paused',
      activeSubs: b.active_subs ?? 0,
      totalUsers: b.total_users ?? 0,
      newUsersToday: b.new_users_today ?? 0,
      // Доход в месяц в рублях по курсу. До 02.10.2026 здесь были центы доллара,
      // подписанные рублями («курс 100»), то есть цифра врала на величину курса.
      mrrRub: Math.round(((b.monthly_income_usd_minor ?? 0) * rate) / 100),
      createdAt: b.created_at,
      siteEnabled: b.site_enabled ?? false,
      siteUrl: b.site_url ?? null,
    }));
  },

  // POST /api/partner/bots/instant-prompt — попросить бэк отправить нам в чат
  // с manager-ботом сообщение с inline-кнопкой t.me/newbot/<m>/<sugg>.
  // Используется как замена прямого openTelegramLink в WebApp (тот не понимает
  // /newbot/... deeplink, а openLink ломает UX через браузер).
  instantBotPrompt: () =>
    tryRequest<{ ok: boolean }>('POST', '/api/partner/bots/instant-prompt', () => ({ ok: true })),

  // POST /api/partner/bots — создать бота через wizard. Бэк требует только {token};
  // brand_name/markup_percent/currency игнорируются и приходят отдельным PATCH'ем после успеха.
  /** Встроен ли платёжный сбор в цену: одна ставка на все способы оплаты. */
  feePolicy: () =>
    request<{ fee_included_in_price: boolean; fee_percent: number }>(
      'GET',
      '/api/partner/me/fee-policy',
    ),
  setFeePolicy: (feeIncludedInPrice: boolean) =>
    request<{ fee_included_in_price: boolean; fee_percent: number }>(
      'PUT',
      '/api/partner/me/fee-policy',
      { fee_included_in_price: feeIncludedInPrice },
    ),

  // Боты, которых монитор погасил по 401 (удалены в BotFather, токен отозван, бан).
  // Только владельцу кабинета. Вернуть: `rotateBotToken`, убрать из списка: `deleteBot`.
  unresponsiveBots: () =>
    request<
      {
        id: string;
        tg_bot_id: number;
        username: string;
        title: string;
        unresponsive_since: string;
        clients: number;
        live_subs: number;
      }[]
    >('GET', '/api/partner/bots/unresponsive'),

  /** Заменить токен у существующего бота: клиенты, подписки и настройки остаются
   *  на месте, бот перезапускается сам в течение нескольких секунд. */
  // `switchBot`: токен другого бота, перенести на него клиентов. Без флага такой токен
  // даёт 409 `app.bots.token_mismatch` с ником нового бота в `context`.
  rotateBotToken: (botId: string, token: string, switchBot = false) =>
    request<unknown>('POST', `/api/partner/bots/${botId}/token`, {
      token,
      switch_bot: switchBot,
    }),

  createBot: async (body: {
    token: string;
    brand_name?: string;
    markup_percent?: number;
    currency?: string;
  }): Promise<PartnerBot> => {
    type BackendBot = {
      id: string;
      tg_bot_id: number;
      username: string;
      title: string;
      is_active: boolean;
      created_at: string;
    };
    const raw = await tryRequest<BackendBot>(
      'POST',
      '/api/partner/bots',
      () => ({
        id: `bot_${Date.now()}`,
        tg_bot_id: 0,
        username: 'pending_bot',
        title: body.brand_name || 'My VPN',
        is_active: true,
        created_at: new Date().toISOString(),
      }),
      { token: body.token },
    );
    return {
      id: raw.id,
      username: raw.username,
      brandName: raw.title,
      status: raw.is_active ? 'active' : 'paused',
      activeSubs: 0,
      totalUsers: 0,
      newUsersToday: 0,
      mrrRub: 0,
      createdAt: raw.created_at,
    };
  },

  // GET /api/partner/bots/{id}
  getBot: (botId: string) =>
    tryRequest<{
      id: string;
      tg_bot_id: number;
      username: string;
      title: string;
      is_active: boolean;
      created_at: string;
      support_url: string | null;
      sub_profile_title: string | null;
      sub_announce: string | null;
      /** Легенда типов серверов под описанием подписки. Нет поля = включена. */
      sub_announce_legend?: boolean;
      support_contact: string | null;
      /** Подпись кнопки мини-аппа у поля ввода (#281). Нет поля (старый бэк) = «🛡 Открыть». */
      menu_button_text?: string;
      menu_button_custom?: boolean;
      loyalty_titles: Record<string, string>;
      banner_url: string | null;
      banners: Record<string, string | null>;
      primary_color: string | null;
      log_channel_id: number | null;
      log_enabled: boolean;
      /** Логи погасил watchdog после пяти отказов подряд, а не партнёр руками. */
      log_auto_disabled?: boolean;
      log_categories: Record<string, boolean> | null;
      /** События раскладываются по темам группы (идея #268). */
      log_topics_enabled?: boolean;
      premium_emoji_enabled: boolean;
      /** Цвет иконок кнопок (ключ из `ICON_COLORS`) или null, пока не выбран. */
      icon_color?: string | null;
      /** Цвета, у которых готовы все иконки бота: только их можно выбрать. */
      icon_colors_available?: string[];
      stickers: Record<string, { emoji: string; added_at: string }>;
      status_page_enabled: boolean;
      /** Двуязычный бот (#274). Нет поля (старый бэк) = выключено. */
      ask_language?: boolean;
      webapp_enabled: boolean;
      mirrors_enabled: boolean;
      /** Клиенты сами выбирают страны в своей подписке (заявка #169). */
      client_country_pick?: boolean;
      /** «−N%» у длинных сроков в боте. Нет поля = показывается (#272). */
      period_discount_badge?: boolean;
      /** Спецтарифы («Белые списки | 3GB») у клиентов. Нет поля = продаются (#272). */
      special_plans_enabled?: boolean;
      /** Тема клиентского мини-аппа: graphite | schema | cloud. Нет поля = graphite. */
      miniapp_theme?: 'graphite' | 'schema' | 'cloud' | 'classic';
      /** Отмеченные сезоны декора и их расписание на сегодня. */
      miniapp_seasons?: MiniappSeason[];
      season_schedule?: SeasonScheduleRow[];
      /** Страница живого превью мини-аппа. */
      miniapp_preview_url?: string | null;
    }>('GET', `/api/partner/bots/${botId}`, () => ({
      id: botId,
      tg_bot_id: 0,
      username: 'unknown_bot',
      title: '',
      is_active: true,
      created_at: new Date().toISOString(),
      support_url: null,
      sub_profile_title: null,
      sub_announce: null,
      support_contact: null,
      loyalty_titles: {},
      banner_url: null,
      banners: {},
      primary_color: null,
      log_channel_id: null,
      log_enabled: true,
      log_categories: null,
      premium_emoji_enabled: false,
      stickers: {},
      status_page_enabled: true,
      webapp_enabled: true,
      mirrors_enabled: true,
    })),

  // DELETE /api/partner/bots/{id}/stickers/{slot} — убрать кастомный стикер события
  deleteBotSticker: (botId: string, slot: string) =>
    request<unknown>('DELETE', `/api/partner/bots/${botId}/stickers/${slot}`),

  // Block A.1: PATCH /api/partner/bots/{id}/log-channel
  updateLogChannel: (
    botId: string,
    body: {
      log_channel_id: number | null;
      log_enabled: boolean;
      log_categories?: Record<string, boolean>;
      /** Раскладывать события по темам группы (идея #268). */
      log_topics_enabled?: boolean;
    },
  ) =>
    tryRequest<{
      id: string;
      log_channel_id: number | null;
      log_enabled: boolean;
      log_categories: Record<string, boolean> | null;
    }>(
      'PATCH',
      `/api/partner/bots/${botId}/log-channel`,
      () => ({
        id: botId,
        log_channel_id: body.log_channel_id,
        log_enabled: body.log_enabled,
        log_categories: body.log_categories ?? null,
      }),
      body,
    ),

  /** Проверить связь с лог-каналом: бот шлёт туда тестовое сообщение и возвращает
   *  диагноз. Раньше тест уходил только при СМЕНЕ канала, и «логи не приходят»
   *  выглядело одинаково при любой причине. */
  testLogChannel: (botId: string) =>
    tryRequest<{ ok: boolean; message: string }>(
      'POST',
      `/api/partner/bots/${botId}/log-channel/test`,
      () => ({ ok: false, message: 'Проверка недоступна в демо-режиме.' }),
    ),

  /** Темы в лог-группе: включены ли в группе, есть ли у бота право, сколько заведено. */
  logTopics: (botId: string) =>
    tryRequest<LogTopicsState>('GET', `/api/partner/bots/${botId}/log-channel/topics`, () =>
      demoLogTopics(),
    ),
  /** Завести все темы разом, не дожидаясь первого события каждой. */
  createLogTopics: (botId: string) =>
    tryRequest<LogTopicsState>('POST', `/api/partner/bots/${botId}/log-channel/topics`, () =>
      demoLogTopics(),
    ),

  // PATCH /api/partner/bots/{id}/premium-emoji — тумблер премиум-эмодзи
  updatePremiumEmoji: (botId: string, enabled: boolean) =>
    tryRequest<{ id: string; premium_emoji_enabled: boolean }>(
      'PATCH',
      `/api/partner/bots/${botId}/premium-emoji`,
      () => ({ id: botId, premium_emoji_enabled: enabled }),
      { enabled },
    ),

  // GET /api/partner/bots/emoji-icons — каталог иконок для выбора значка (один на все боты, тяжёлый)
  emojiIcons: () =>
    tryRequest<EmojiIcons>('GET', '/api/partner/bots/emoji-icons', () => ({ icons: [], palette: [] })),

  // PATCH /api/partner/bots/{id}/icon-color — цвет иконок кнопок; null убирает цвет
  /** Шаблоны оформления и что из каждого можно применить к этому боту. */
  botTemplates: (botId: string) =>
    request<BotTemplatesResponse>('GET', `/api/partner/bot-templates?bot_id=${botId}`),
  /** 8 картинок шаблона с именем в плашке, уменьшенные под превью (data-URI). */
  botTemplatePreview: (templateId: string, variantId: string, name: string) =>
    request<{ name: string; banners: Record<string, string> }>(
      'GET',
      `/api/partner/bot-templates/${templateId}/${variantId}/preview?name=${encodeURIComponent(name)}`,
    ),
  /** Эмодзи в цвете варианта той же функцией, что красит сообщения бота. */
  botTemplateColorize: (body: { color: string | null; html: string[]; buttons: string[] }) =>
    request<{ html: string[]; buttons: Array<{ text: string; icon_id: string | null }> }>(
      'POST',
      '/api/partner/bot-templates/colorize',
      body,
    ),
  applyBotTemplate: (botId: string, body: BotTemplateApplyBody) =>
    request<{ application_id: string; applied: BotTemplatePart[]; skipped: Record<string, string> }>(
      'POST',
      `/api/partner/bots/${botId}/template`,
      body,
    ),
  revertBotTemplate: (botId: string) =>
    request<{ reverted: string[]; kept: string[] }>('POST', `/api/partner/bots/${botId}/template/revert`),
  updateIconColor: (botId: string, color: string | null) =>
    tryRequest<IconColorResponse>(
      'PATCH',
      `/api/partner/bots/${botId}/icon-color`,
      () => ({ id: botId, icon_color: color }),
      { color },
    ),

  // #2 — выбор платёжек, которые видят клиенты бота
  getMenuLayout: (botId: string) =>
    request<MenuLayoutResponse>('GET', `/api/partner/bots/${botId}/menu-layout`),
  updateMenuLayout: (
    botId: string,
    body: { section: string; rows: string[][]; hidden: string[] },
  ) => request<MenuLayoutResponse>('PUT', `/api/partner/bots/${botId}/menu-layout`, body),
  /** Цвет встроенной кнопки (заявка #171). `style=null` — вернуть наш. */
  setButtonStyle: (botId: string, body: { section: string; slot: string; style: string | null }) =>
    request<MenuLayoutResponse>('PATCH', `/api/partner/bots/${botId}/button-style`, body),
  /** Где открывается встроенная кнопка (заявка #181). `target=null` — в чате бота. */
  setButtonAction: (botId: string, body: { section: string; slot: string; target: string | null }) =>
    request<MenuLayoutResponse>('PATCH', `/api/partner/bots/${botId}/button-action`, body),
  getPaymentProviders: (botId: string) =>
    tryRequest<{
      available: string[];
      enabled: string[];
      recurrent_available?: boolean;
      recurrent_enabled?: boolean;
    }>(
      'GET',
      `/api/partner/bots/${botId}/payment-providers`,
      () => ({
        available: ['cryptobot', 'lava', 'heleket', 'lzt', 'xrocket', 'aurapay', 'aurapay_card', 'stars'],
        enabled: ['cryptobot', 'lava', 'heleket', 'lzt', 'xrocket', 'aurapay', 'aurapay_card', 'stars'],
      }),
    ),
  updatePaymentProviders: (botId: string, enabled: string[]) =>
    request<{ available: string[]; enabled: string[] }>(
      'PATCH',
      `/api/partner/bots/${botId}/payment-providers`,
      { enabled_providers: enabled },
    ),
  updateRecurrent: (botId: string, enabled: boolean) =>
    request<{
      available: string[];
      enabled: string[];
      recurrent_available?: boolean;
      recurrent_enabled?: boolean;
    }>('PATCH', `/api/partner/bots/${botId}/recurrent`, { enabled }),

  // ----- Документы бота (#208): свои ссылки партнёра и наши шаблоны -----
  getLegalDocs: (botId: string) =>
    request<{
      terms_url: string | null;
      privacy_url: string | null;
      template_terms_url: string | null;
      template_privacy_url: string | null;
    }>('GET', `/api/partner/bots/${botId}/legal-docs`),
  saveLegalDocs: (botId: string, body: { terms_url: string | null; privacy_url: string | null }) =>
    request<{
      terms_url: string | null;
      privacy_url: string | null;
      template_terms_url: string | null;
      template_privacy_url: string | null;
    }>('PUT', `/api/partner/bots/${botId}/legal-docs`, body),

  // ----- Обязательная подписка клиентов на каналы -----
  getRequiredChannels: (botId: string) =>
    tryRequest<RequiredChannelsState>(
      'GET',
      `/api/partner/bots/${botId}/required-channels`,
      () => ({ enabled: false, channels: [] }),
    ),
  // POST — добавить канал (@username/ссылка/ID). Ошибки бэка (не админ/лимит) пробрасываются.
  addRequiredChannel: (botId: string, channel: string) =>
    request<RequiredChannelsState>(
      'POST',
      `/api/partner/bots/${botId}/required-channels`,
      { channel },
    ),
  removeRequiredChannel: (botId: string, channelId: number) =>
    request<RequiredChannelsState>(
      'DELETE',
      `/api/partner/bots/${botId}/required-channels/${channelId}`,
    ),
  toggleRequiredChannels: (botId: string, enabled: boolean) =>
    request<RequiredChannelsState>(
      'PATCH',
      `/api/partner/bots/${botId}/required-channels`,
      { enabled },
    ),
  // Бот звёзд партнёра на Хексе: шаг «1. Купить звёзды» на экране оплаты звёздами.
  getStarsShop: (botId: string) =>
    request<StarsShop>('GET', `/api/partner/bots/${botId}/stars-shop`),
  setStarsShop: (botId: string, bot: string | null) =>
    request<StarsShop>('PUT', `/api/partner/bots/${botId}/stars-shop`, { bot }),
  // Пробник только подписчикам этих же каналов (#227), отдельно от закрытия бота.
  toggleTrialChannels: (botId: string, enabled: boolean) =>
    request<RequiredChannelsState>(
      'PATCH',
      `/api/partner/bots/${botId}/required-channels/trial`,
      { enabled },
    ),

  // PATCH /api/partner/bots/{id}
  updateBot: (
    botId: string,
    body: { title?: string; support_url?: string; sub_profile_title?: string; sub_announce?: string; sub_announce_legend?: boolean; support_contact?: string; primary_color?: string; loyalty_titles?: Record<string, string>; status_page_enabled?: boolean; ask_language?: boolean; webapp_enabled?: boolean; mirrors_enabled?: boolean; client_country_pick?: boolean; period_discount_badge?: boolean; special_plans_enabled?: boolean; miniapp_theme?: MiniappTheme; miniapp_seasons?: MiniappSeason[] },
  ) =>
    tryRequest<{
      id: string;
      title: string;
      support_url: string | null;
      sub_profile_title: string | null;
      sub_announce: string | null;
      sub_announce_legend?: boolean;
      support_contact: string | null;
      /** Подпись кнопки мини-аппа у поля ввода (#281). Нет поля (старый бэк) = «🛡 Открыть». */
      menu_button_text?: string;
      menu_button_custom?: boolean;
      loyalty_titles: Record<string, string>;
      banner_url: string | null;
      primary_color: string | null;
    }>('PATCH', `/api/partner/bots/${botId}`, () => ({
      id: botId,
      title: body.title || '',
      support_url: body.support_url ?? null,
      sub_profile_title: body.sub_profile_title ?? null,
      sub_announce: body.sub_announce ?? null,
      support_contact: body.support_contact ?? null,
      loyalty_titles: body.loyalty_titles ?? {},
      banner_url: null,
      primary_color: body.primary_color ?? null,
    }), body),

  // ---------- Отзывы ----------
  reviews: (botId: string, params?: { status?: ReviewStatusFilter; cursor?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.cursor) qs.set('cursor', params.cursor);
    const suffix = qs.toString() ? `?${qs}` : '';
    return tryRequest<PartnerReviewsPage>(
      'GET',
      `/api/partner/bots/${botId}/reviews${suffix}`,
      () => ({ items: [], next_cursor: null, has_more: false, counts: { published: 0, hidden: 0, total: 0 } }),
    );
  },

  // Модерация и ручные отзывы — мутации, поэтому обычный request без мок-фолбэка:
  // «скрыл» в моке выглядело бы как сработавшее действие при упавшем бэке.
  moderateReview: (botId: string, reviewId: string, status: 'published' | 'hidden') =>
    request<PartnerReview>('PATCH', `/api/partner/bots/${botId}/reviews/${reviewId}`, { status }),

  // Ответ на отзыв: виден всем под отзывом, автору уходит сообщение в бота.
  // Пустой текст снимает ответ.
  replyReview: (botId: string, reviewId: string, text: string) =>
    request<PartnerReview>('PUT', `/api/partner/bots/${botId}/reviews/${reviewId}/reply`, { text }),

  createManualReview: (botId: string, body: ManualReviewInput) =>
    request<PartnerReview>('POST', `/api/partner/bots/${botId}/reviews`, body),

  deleteReview: (botId: string, reviewId: string) =>
    request<void>('DELETE', `/api/partner/bots/${botId}/reviews/${reviewId}`),

  reviewsConfig: (botId: string) =>
    tryRequest<ReviewsConfig>('GET', `/api/partner/bots/${botId}/reviews/config`, () => ({
      enabled: true,
      glyph: 'shield-check',
      verified_badge: true,
      ask_after_days: 5,
      promo: { enabled: false, title: '', subtitle: '' },
      reward: { enabled: false, percent: 10 },
    })),

  updateReviewsConfig: (botId: string, body: Partial<ReviewsConfig>) =>
    request<ReviewsConfig>('PATCH', `/api/partner/bots/${botId}/reviews/config`, body),

  // GET /api/partner/bots/{id}/servers — имена серверов подписки + переименования + скрытия
  botServers: (botId: string) =>
    tryRequest<{ servers: string[]; overrides: Record<string, string>; hidden: string[] }>(
      'GET',
      `/api/partner/bots/${botId}/servers`,
      () => ({ servers: [], overrides: {}, hidden: [] }),
    ),

  // PATCH /api/partner/bots/{id}/server-names — переименования (null = сброс) и скрытия.
  // `hidden` передаётся ЦЕЛИКОМ (снятая галка = сервера нет в списке); undefined — не трогать.
  updateBotServerNames: (
    botId: string,
    overrides: Record<string, string | null>,
    hidden?: string[],
  ) =>
    request<{ servers: string[]; overrides: Record<string, string>; hidden: string[] }>(
      'PATCH',
      `/api/partner/bots/${botId}/server-names`,
      hidden === undefined ? { overrides } : { overrides, hidden },
    ),

  // GET /api/partner/bots/{id}/texts — каталог настраиваемых текстов/кнопок
  getBotTexts: (botId: string, phase: number | null = 1) =>
    tryRequest<TextCatalog>(
      'GET',
      `/api/partner/bots/${botId}/texts?locale=${textsLocale}${phase != null ? `&phase=${phase}` : ''}`,
      () => ({ entries: [], groups: [], premium_emoji_enabled: true }),
    ),

  // PATCH /api/partner/bots/{id}/texts — сохранить/сбросить override текстов и кнопок (null = сброс)
  updateBotTexts: (botId: string, overrides: Record<string, string | null>) =>
    request<TextCatalog>('PATCH', `/api/partner/bots/${botId}/texts`, {
      overrides,
      locale: textsLocale,
    }),

  // POST /api/partner/bots/{id}/texts/validate — проверить пачку без сохранения (#231)
  validateBotTexts: (botId: string, overrides: Record<string, string | null>) =>
    request<{ ok: boolean; results: Array<{ key: string; ok: boolean; error?: string | null; text?: string | null }> }>(
      'POST',
      `/api/partner/bots/${botId}/texts/validate`,
      { overrides },
    ),

  // PUT /api/partner/bots/{id}/texts/name-fallback — слово вместо {first_name} без имени
  setNameFallback: (botId: string, value: string) =>
    request<TextCatalog>('PUT', `/api/partner/bots/${botId}/texts/name-fallback`, { value }),

  // PATCH /api/partner/bots/{id}/button-icon — премиум-иконка кнопки (emoji_id=null → убрать)
  /** `disabled=true` — снять значок совсем (кнопка без иконки). Пустой `emoji_id`
   *  без этого флага возвращает наш дефолт, а не убирает значок. */
  updateButtonIcon: (
    botId: string,
    key: string,
    emoji_id: string | null,
    glyph?: string | null,
    disabled = false,
  ) =>
    request<TextCatalog>('PATCH', `/api/partner/bots/${botId}/button-icon`, {
      key,
      emoji_id,
      disabled,
      glyph,
    }),

  // PATCH /api/partner/bots/{id}/emoji — подмена значка (glyph=null → вернуть дефолтный).
  // В отличие от button-icon работает и на системных ключах: там нельзя менять текст,
  // но можно значок.
  updateEmojiOverride: (
    botId: string,
    key: string,
    glyph: string | null,
    emoji_id?: string | null,
  ) =>
    request<TextCatalog>('PATCH', `/api/partner/bots/${botId}/emoji`, {
      key,
      glyph,
      emoji_id,
    }),

  // GET /api/partner/bots/{id}/warmup — сценарии прогрева клиентов
  // Свои автосообщения (#207). Воронка «Путь клиента» живёт на сайте, здесь список и
  // правка: в ответе `journey` есть и правила, и каталог сценариев.
  getJourney: (botId: string, days: 7 | 30 | 90 = 30) =>
    request<Journey>('GET', `/api/partner/bots/${botId}/journey?days=${days}`),
  createWarmupRule: (botId: string, body: WarmupRuleInput) =>
    request<WarmupRule>('POST', `/api/partner/bots/${botId}/warmup-rules`, body),
  updateWarmupRule: (botId: string, ruleId: string, body: Partial<WarmupRuleInput>) =>
    request<WarmupRule>('PATCH', `/api/partner/bots/${botId}/warmup-rules/${ruleId}`, body),
  deleteWarmupRule: (botId: string, ruleId: string) =>
    request<void>('DELETE', `/api/partner/bots/${botId}/warmup-rules/${ruleId}`),

  getBotWarmup: (botId: string) =>
    tryRequest<WarmupCatalog>('GET', `/api/partner/bots/${botId}/warmup`, () => ({
      triggers: [],
      groups: [],
    })),

  // PATCH /api/partner/bots/{id}/warmup — один сценарий: текст, тумблер, картинка.
  // Непереданные поля не трогаем, null — сброс к нашему тексту / снять картинку.
  updateBotWarmup: (
    botId: string,
    patch: {
      key: string;
      text?: string | null;
      enabled?: boolean;
      media_url?: string | null;
      /** Персональная скидка к этому сообщению, %. 0 — выключить. */
      offer_percent?: number | null;
    },
  ) => request<WarmupCatalog>('PATCH', `/api/partner/bots/${botId}/warmup`, patch),

  // POST /api/partner/bots/{id}/warmup/image (multipart) — картинка к сценарию
  uploadWarmupImage: async (botId: string, key: string, file: File): Promise<WarmupCatalog> => {
    if (!API_BASE) {
      throw new ApiError(0, null, 'VITE_API_BASE not configured — загрузка картинок требует бэкенд');
    }
    const { token } = await authenticate();
    const fd = new FormData();
    fd.append('key', key);
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/api/partner/bots/${botId}/warmup/image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new ApiError(res.status, err, `uploadWarmupImage → ${res.status}`);
    }
    return res.json();
  },

  // GET /api/partner/bots/{id}/webapp-texts — каталог настраиваемых текстов мини-аппа
  getWebappTexts: (botId: string) =>
    tryRequest<WebappTextCatalog>(
      'GET',
      `/api/partner/bots/${botId}/webapp-texts`,
      () => ({ entries: [], groups: [] }),
    ),

  // PATCH /api/partner/bots/{id}/webapp-texts — сохранить/сбросить override текстов мини-аппа (null = сброс)
  updateWebappTexts: (botId: string, overrides: Record<string, string | null>) =>
    request<WebappTextCatalog>('PATCH', `/api/partner/bots/${botId}/webapp-texts`, { overrides }),

  // POST /api/partner/bots/{id}/active (form-data) — toggle
  setBotActive: async (botId: string, is_active: boolean): Promise<{ id: string; is_active: boolean }> => {
    if (!API_BASE) {
      if (USE_MOCK_FALLBACK) return { id: botId, is_active };
      throw new ApiError(0, null, 'VITE_API_BASE not configured');
    }
    const { token } = await authenticate();
    const fd = new FormData();
    fd.append('is_active', String(is_active));
    const res = await fetch(`${API_BASE}/api/partner/bots/${botId}/active`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      if (USE_MOCK_FALLBACK) return { id: botId, is_active };
      throw new ApiError(res.status, err, `setBotActive → ${res.status}`);
    }
    return res.json();
  },

  // POST /api/partner/bots/{id}/banner?section=<sec> (multipart) — upload banner photo
  uploadBotBanner: async (
    botId: string,
    file: File,
    section: string = 'main',
  ): Promise<{ banner_url: string | null; banners: Record<string, string | null> }> => {
    if (!API_BASE) {
      throw new ApiError(0, null, 'VITE_API_BASE not configured — banner uploads need real backend');
    }
    const { token } = await authenticate();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/api/partner/bots/${botId}/banner?section=${encodeURIComponent(section)}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new ApiError(res.status, err, `uploadBotBanner → ${res.status}`);
    }
    return res.json();
  },

  // DELETE /api/partner/bots/{id}/banner?section=<sec>
  /** `disable=true` — раздел БЕЗ картинки (в базе явный null). По умолчанию ключ
   *  просто убирается, и раздел наследует главный баннер. */
  deleteBotBanner: async (
    botId: string,
    section: string = 'main',
    disable = false,
  ): Promise<{ banner_url: string | null; banners: Record<string, string | null> }> => {
    if (!API_BASE) {
      throw new ApiError(0, null, 'VITE_API_BASE not configured');
    }
    const { token } = await authenticate();
    const res = await fetch(`${API_BASE}/api/partner/bots/${botId}/banner?section=${encodeURIComponent(section)}&disable=${disable}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new ApiError(res.status, err, `deleteBotBanner → ${res.status}`);
    }
    return res.json();
  },

  // DELETE /api/partner/bots/{id}
  deleteBot: (botId: string) =>
    tryRequest<void>('DELETE', `/api/partner/bots/${botId}`, () => undefined),

  // GET /api/partner/bots/{id}/stats
  botStats: (botId: string) =>
    tryRequest<{
      period_days: number;
      active_subscriptions: number;
      new_subscriptions: number;
      expired_subscriptions: number;
      revenue_minor: number;
      currency: string;
      avg_check_minor: number;
    }>('GET', `/api/partner/bots/${botId}/stats`, () => ({
      period_days: 30,
      active_subscriptions: 0,
      new_subscriptions: 0,
      expired_subscriptions: 0,
      revenue_minor: 0,
      currency: 'USD',
      avg_check_minor: 0,
    })),

  // GET /api/partner/referrals/subpartners/stats — L1/L2 разбивка
  subpartnerStats: () =>
    tryRequest<{
      l1: { level: number; referred_count: number; bonus_earned_minor: number };
      l2: { level: number; referred_count: number; bonus_earned_minor: number };
      currency: string;
    }>('GET', '/api/partner/referrals/subpartners/stats', () => ({
      l1: { level: 1, referred_count: 0, bonus_earned_minor: 0 },
      l2: { level: 2, referred_count: 0, bonus_earned_minor: 0 },
      currency: 'USD',
    })),

  // GET /api/partner/referrals/subpartners/list
  subpartnerList: () =>
    tryRequest<
      Array<{ partner_id: string; display_name: string; level: number; joined_at: string }>
    >('GET', '/api/partner/referrals/subpartners/list', () => []),

  // GET /api/partner/bots/{id}/pricing — реальный backend shape:
  // {mode, markup_basis_points, custom_prices_display_minor, plans[{plan_uuid, plan_name,
  // days, devices, platform_price_minor, effective_retail_minor}]}.
  pricing: (botId: string) =>
    tryRequest<{
      mode: 'default' | 'percent' | 'custom';
      markup_basis_points: number;
      custom_prices_display_minor: Record<string, number>;
      plans: Array<{
        plan_uuid: string;
        plan_name: string;
        days: number;
        devices: number;
        platform_price_minor: number;
        effective_retail_minor: number;
        /** Спецтариф с жёсткой ценой владельца: наценка к нему не применяется,
         *  поэтому в ценовом редакторе его не показываем, а в брендинге показываем. */
        is_special?: boolean;
        /** Та самая жёсткая цена в копейках, только у спецтарифа. */
        fixed_retail_display_minor?: number | null;
        /** Продано за 30 дней в этом боте (первые покупки, без пробников). */
        sales_30d?: number;
      }>;
      markup_basis_points_traffic: number;
      /** Пакеты докупки GB по цене ДО партнёрской наценки (USD-cents + ₽-копейки). */
      traffic_packs: Array<{
        gb: number;
        platform_price_usd_minor: number;
        platform_price_display_minor: number;
      }>;
      plan_name_overrides: Record<string, string>;
      plan_emoji_overrides?: Record<string, string>;
      /** Семейства тарифов, убранные с витрины бота: `['5','15']` по числу устройств. */
      disabled_plan_families?: string[];
      /** Сроки, снятые с продажи целиком: [365]. */
      hidden_plan_periods?: number[];
      /** Отдельные тарифы, снятые с продажи: ['15:14'] (устройства:дни). */
      hidden_plan_cells?: string[];
      /** «Популярно»: 'auto' | 'none' | число устройств строкой. */
      popular_plan?: string;
      /** Какое семейство сейчас получает бейдж. null — бейджа нет. */
      popular_devices?: number | null;
      /** Раздел «Навсегда». null: на платформе он ещё не запущен, блок не рисуем. */
      lifetime?: LifetimePricingBlock | null;
    }>('GET', `/api/partner/bots/${botId}/pricing`, () => ({
      mode: 'default',
      markup_basis_points: 0,
      custom_prices_display_minor: {},
      plans: [],
      markup_basis_points_traffic: 0,
      traffic_packs: [],
      plan_name_overrides: {},
    })),

  // PUT /api/partner/bots/{id}/pricing.
  savePricing: (
    botId: string,
    body: {
      mode: 'default' | 'percent' | 'custom';
      markup_basis_points?: number | null;
      custom_prices_display_minor?: Record<string, number> | null;
      markup_basis_points_traffic?: number | null;
      plan_name_overrides?: Record<string, string> | null;
      plan_emoji_overrides?: Record<string, string> | null;
      /** null — не менять, [] — показать все. Спрятать все сразу бэк отобьёт. */
      disabled_plan_families?: string[] | null;
      /** Скрытые сроки в днях. null — не менять, [] — показать все. */
      hidden_plan_periods?: number[] | null;
      /** Скрытые тарифы 'устройства:дни'. null — не менять, [] — показать все. */
      hidden_plan_cells?: string[] | null;
      /** 'auto' | 'none' | число устройств. Не передан: не менять. */
      popular_plan?: string | null;
      /** Тумблер раздела «Навсегда» у бота. Не передан: не менять. */
      lifetime_enabled?: boolean | null;
      /** Наценка на докупки к «Навсегда», bp. Не передана: остаётся как есть (в том
       *  числе дефолт платформы, если своей не было). */
      markup_basis_points_lifetime_addons?: number | null;
    },
  ) =>
    tryRequest<{
      mode: string;
      markup_basis_points: number;
      custom_prices_display_minor: Record<string, number>;
      plans: Array<unknown>;
      markup_basis_points_traffic: number;
      plan_name_overrides: Record<string, string>;
    }>(
      'PUT',
      `/api/partner/bots/${botId}/pricing`,
      () => ({
        mode: body.mode,
        markup_basis_points: body.markup_basis_points ?? 0,
        custom_prices_display_minor: body.custom_prices_display_minor ?? {},
        plans: [],
        markup_basis_points_traffic: body.markup_basis_points_traffic ?? 0,
        plan_name_overrides: body.plan_name_overrides ?? {},
      }),
      body,
    ),

  // GET /api/partner/subscriptions — страница с keyset-курсором.
  //
  // Раньше метод не передавал НИ одного параметра, а бэк отдавал 100 записей по
  // умолчанию: у партнёра с >100 продаж старые подписки просто не доезжали, и это
  // выглядело как «подписки пропали». Теперь фильтры считает БД, а страницы
  // догружаются курсором (см. useInfiniteQuery в routes/subscriptions.tsx).
  subscriptions: async (params: PartnerSubscriptionsQuery = {}): Promise<PartnerSubscriptionsPage> => {
    type BackendSub = {
      id: string;
      adaptgroup_uuid: string;
      plan_name: string;
      client_tg_id: number | null;
      client_name: string;
      status: 'active' | 'expired' | 'frozen';
      days_left: number;
      end_date: string;
      frozen_at: string | null;
      created_at: string;
      price_paid_minor: number;
      partner_earned_minor: number;
      is_trial: boolean;
      is_special: boolean;
      is_migrated: boolean;
      channel?: string | null;
      is_lifetime?: boolean;
      supplier?: string;
    };
    type BackendPage = { items: BackendSub[]; next_cursor: string | null; has_more: boolean };

    const qs = new URLSearchParams();
    if (params.status && params.status !== 'all') qs.set('status', params.status);
    if (params.kind && params.kind !== 'all') qs.set('kind', params.kind);
    if (params.botId) qs.set('bot_id', params.botId);
    if (params.q?.trim()) qs.set('q', params.q.trim());
    if (params.clientId) qs.set('client_id', params.clientId);
    if (params.cursor) qs.set('cursor', params.cursor);
    qs.set('limit', String(params.limit ?? 50));

    // /api/partner/subscriptions отдаёт суммы в USD-cents (price_paid_minor,
    // partner_earned_minor), отдельного display-поля у него нет. FX-курс берём из
    // /api/partner/me (usd_to_display_rate, CBR-обновляется) — тот же источник, что
    // и в me()/dashboard. cents × rate / 100 = display-валюта (RUB по дефолту).
    // Раньше тут был «грубый» /100 без FX → партнёр видел суммы заниженные в ~70-90 раз.
    const [page, fxRate] = await Promise.all([
      tryRequest<BackendPage>('GET', `/api/partner/subscriptions?${qs}`, () => ({
        items: [],
        next_cursor: null,
        has_more: false,
      })),
      usdToDisplayRate(),
    ]);
    return {
      items: page.items.map((s) => ({
        id: s.id,
        adaptgroupUuid: s.adaptgroup_uuid || '',
        planName: s.plan_name,
        clientTgId: s.client_tg_id,
        clientName: s.client_name,
        status: s.status,
        daysLeft: s.days_left,
        expiresAt: s.end_date,
        frozenAt: s.frozen_at ?? null,
        createdAt: s.created_at || '',
        pricePaidRub: Math.round((s.price_paid_minor * fxRate) / 100),
        partnerEarnedRub: Math.round((s.partner_earned_minor * fxRate) / 100),
        isTrial: Boolean(s.is_trial),
        isSpecial: Boolean(s.is_special),
        isMigrated: Boolean(s.is_migrated),
        channel: s.channel ?? null,
        isLifetime: Boolean(s.is_lifetime),
        supplier: s.supplier ?? 'adaptgroup',
      })),
      nextCursor: page.next_cursor,
      hasMore: page.has_more,
    };
  },

  // GET /api/partner/subscriptions/counts — счётчики вкладок по всей базе партнёра,
  // а не по загруженной странице. Именно они показывают, что ничего не потерялось.
  subscriptionCounts: (
    params: Pick<PartnerSubscriptionsQuery, 'botId' | 'q' | 'kind' | 'clientId'> = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.botId) qs.set('bot_id', params.botId);
    if (params.q?.trim()) qs.set('q', params.q.trim());
    if (params.clientId) qs.set('client_id', params.clientId);
    // Вид (платные/триал) влияет на статусные счётчики: бейдж «Все» должен считать
    // ровно то, что видно в списке под ним.
    if (params.kind) qs.set('kind', params.kind);
    return tryRequest<PartnerSubscriptionCounts>(
      'GET',
      `/api/partner/subscriptions/counts?${qs}`,
      () => ({ total: 0, active: 0, expiring: 0, expired: 0, frozen: 0, paid: 0, trial: 0, special: 0 }),
    );
  },

  // GET /api/partner/clients — все клиенты ботов партнёра, а не только купившие:
  // раздел «Клиенты». Кто зарегистрировался на сайте и не купил, раньше не был виден.
  clients: async (params: PartnerClientsQuery = {}): Promise<PartnerClientsPage> => {
    type BackendClient = {
      id: string;
      name: string;
      tg_user_id: number | null;
      email: string | null;
      external_id: string | null;
      origin: string;
      bot_username: string | null;
      created_at: string;
      active_subscriptions: number;
      subscriptions_total: number;
      is_blocked: boolean;
    };
    const qs = new URLSearchParams();
    if (params.origin && params.origin !== 'all') qs.set('origin', params.origin);
    if (params.buy && params.buy !== 'all') qs.set('buy', params.buy);
    if (params.q?.trim()) qs.set('q', params.q.trim());
    if (params.cursor) qs.set('cursor', params.cursor);
    qs.set('limit', String(params.limit ?? 50));
    // Через tryRequest, как у соседних списков: только он превращает 401/403 в
    // AuthError, и мёртвая сессия даёт экран повторного входа, а не «Повторить».
    const page = await tryRequest<{ items: BackendClient[]; next_cursor: string | null; has_more: boolean }>(
      'GET',
      `/api/partner/clients?${qs}`,
      () => ({ items: [], next_cursor: null, has_more: false }),
    );
    return {
      items: page.items.map((c) => ({
        id: c.id,
        name: c.name,
        tgUserId: c.tg_user_id,
        email: c.email,
        externalId: c.external_id,
        origin: c.origin,
        botUsername: c.bot_username,
        createdAt: c.created_at,
        activeSubscriptions: c.active_subscriptions,
        subscriptionsTotal: c.subscriptions_total,
        isBlocked: Boolean(c.is_blocked),
      })),
      nextCursor: page.next_cursor,
      hasMore: page.has_more,
    };
  },

  clientCounts: async (params: Pick<PartnerClientsQuery, 'q'> = {}): Promise<PartnerClientCounts> => {
    const qs = new URLSearchParams();
    if (params.q?.trim()) qs.set('q', params.q.trim());
    const c = await tryRequest<{
      total: number;
      telegram: number;
      web: number;
      api: number;
      with_active: number;
      never_bought: number;
    }>('GET', `/api/partner/clients/counts?${qs}`, () => ({
      total: 0,
      telegram: 0,
      web: 0,
      api: 0,
      with_active: 0,
      never_bought: 0,
    }));
    return {
      total: c.total,
      telegram: c.telegram,
      web: c.web,
      api: c.api,
      withActive: c.with_active,
      neverBought: c.never_bought,
    };
  },

  // GET /api/partner/subscriptions/{id} — деталь с live-трафиком (лениво по тапу).
  giftQuote: (id: string, days: number) =>
    request<{
      days: number;
      cost_usd_minor: number;
      cost_display_minor: number;
      display_currency: string;
      spendable_usd_minor: number;
      paid_usd_minor: number;
      revenue_usd_minor: number;
      enough: boolean;
    }>('GET', `/api/partner/subscriptions/${id}/gift-quote?days=${days}`),

  /** `requestId` — ключ одного нажатия: повтор с ним не списывает второй раз. */
  giftDays: (id: string, days: number, notify = true, requestId?: string) =>
    request<{
      subscription_id: string;
      days: number;
      charged_usd_minor: number;
      end_date: string;
      notified: boolean;
    }>('POST', `/api/partner/subscriptions/${id}/gift`, {
      days,
      notify,
      ...(requestId ? { request_id: requestId } : {}),
    }),

  subscriptionDetail: async (id: string): Promise<PartnerSubscriptionDetail> => {
    type BackendDetail = {
      id: string;
      adaptgroup_uuid: string;
      plan_name: string;
      status: 'active' | 'expired' | 'frozen';
      is_trial: boolean;
      is_special: boolean;
      is_migrated: boolean;
      created_at: string;
      end_date: string;
      days_left: number;
      frozen_at: string | null;
      days: number;
      devices: number;
      used_traffic_bytes: number | null;
      traffic_limit_bytes: number | null;
      channel?: string | null;
      is_lifetime?: boolean;
      supplier?: string;
    };
    const d = await request<BackendDetail>('GET', `/api/partner/subscriptions/${id}`);
    return {
      id: d.id,
      adaptgroupUuid: d.adaptgroup_uuid,
      planName: d.plan_name,
      status: d.status,
      isTrial: Boolean(d.is_trial),
      isSpecial: Boolean(d.is_special),
      isMigrated: Boolean(d.is_migrated),
      createdAt: d.created_at,
      expiresAt: d.end_date,
      daysLeft: d.days_left,
      frozenAt: d.frozen_at,
      days: d.days,
      devices: d.devices,
      usedTrafficBytes: d.used_traffic_bytes,
      trafficLimitBytes: d.traffic_limit_bytes,
      channel: d.channel ?? null,
      isLifetime: Boolean(d.is_lifetime),
      supplier: d.supplier ?? 'adaptgroup',
    };
  },

  // GET /api/partner/finance/transactions. Backend → {id, amount_minor, currency,
  // reason, description, reference_type, reference_id, created_at}. UI ждёт
  // {id, kind, amountRub, description, timestamp}.
  walletTransactions: async (): Promise<WalletTransaction[]> => {
    type BackendTx = {
      id: string;
      amount_minor: number;
      amount_display_minor?: number;
      currency: string;
      display_currency?: string;
      reason: string;
      description: string | null;
      reference_type: string | null;
      reference_id: string | null;
      created_at: string;
      pocket?: 'earned' | 'paid';
      tx_id?: string;
    };
    const rows = await tryRequest<BackendTx[]>(
      'GET',
      '/api/partner/finance/transactions',
      () => [],
    );
    // reason → русский label. Бэк использует английские enum-строки, юзеру нужны
    // понятные надписи. Если описание не пустое — оставляем (там часто конкретика
    // про конкретную подписку), иначе fallback на маппинг по reason.
    const reasonLabels: Record<string, string> = {
      subscription_purchase: 'Покупка подписки',
      subscription_renewal: 'Продление подписки',
      subscription_upgrade: 'Апгрейд подписки',
      traffic_purchase: 'Покупка трафика',
      devices_purchase: 'Покупка устройств',
      topup: 'Пополнение баланса',
      payout_request: 'Запрос на вывод',
      payout_completed: 'Вывод выполнен',
      refund: 'Возврат',
      provider_fee: 'Комиссия платёжки',
      adaptgroup_cost: 'Себестоимость провайдера',
      platform_markup_revenue: 'Платформенная наценка',
      partner_markup_revenue: 'Партнёрская наценка',
      referral_bonus: 'Реферальный бонус',
      promo_discount: 'Промо-скидка',
      adjustment: 'Корректировка баланса',
      // Раздел «Балансы» админки: деньги, заплаченные напрямую, и перенос между кошельками.
      manual_topup: 'Операция платформы',
      wallet_transfer: 'Перенос между балансами',
      voucher_purchase: 'Покупка ваучера',
      voucher_redeem: 'Активация ваучера',
      voucher_expire: 'Ваучер истёк',
      voucher_cancel: 'Возврат за ваучер',
      // Партнёр сам перевёл клиенту его реферальный заработок, сумма вернулась в доход.
      client_withdraw_paid: 'Возмещение за вывод клиента',
      // Этих причин в словаре не было вовсе, и партнёр видел машинный код латиницей.
      partner_topup: 'Пополнение счётом',
      giveaway_escrow: 'Резерв на розыгрыш',
      giveaway_prize: 'Приз розыгрыша',
      giveaway_refund: 'Возврат с розыгрыша',
      mirror_bonus: 'Доля с магазина',
    };

    // Описания которые приходят с бэка (тоже на английском в основном).
    // Переводим простые/частые.
    const translateDescription = (raw: string): string => {
      const lc = raw.toLowerCase();
      // Откат ошибочного начисления («reversal: phantom recurrent charge …»). Причина у
      // него та же, что у исходной операции, и без этой строки откат подписывался
      // «Продление подписки −133 ₽», как будто с партнёра списали за продление.
      if (lc.startsWith('reversal')) return 'Отмена ошибочного начисления';
      // Розыгрыши и ваучеры пишут описание с числом, и без этих строк партнёр видел в
      // истории «giveaway escrow x2», а отмену ваучера называло «Партнёрский ваучер».
      const prizes = (n: number) => {
        const m10 = n % 10;
        const m100 = n % 100;
        const word =
          m10 === 1 && m100 !== 11
            ? 'приз'
            : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)
              ? 'приза'
              : 'призов';
        return `${n} ${word}`;
      };
      // Доля партнёра с докупки к «Навсегда»: «lifetime partner +50 GB».
      const lifetimeAddon = /^lifetime partner \+(\d+) (gb|devices)/.exec(lc);
      if (lifetimeAddon) {
        const n = Number(lifetimeAddon[1]);
        return lifetimeAddon[2] === 'gb'
          ? `Докупка к «Навсегда»: +${n} ГБ`
          : `Докупка к «Навсегда»: +${n} устройств`;
      }
      if (lc.startsWith('client referral withdrawal paid')) return 'Возмещение за вывод клиента';
      // Списание реферальных бонусов клиентам из маржи партнёра (`referrals/service.py`).
      if (lc.startsWith('referrals deduct')) return 'Реферальные бонусы клиентам';
      const escrowTopUp = /^giveaway escrow top-up \+(\d+)/.exec(lc);
      if (escrowTopUp) return `Резерв на розыгрыш: ещё ${prizes(Number(escrowTopUp[1]))}`;
      const escrow = /^giveaway escrow x(\d+)/.exec(lc);
      if (escrow) return `Резерв на розыгрыш: ${prizes(Number(escrow[1]))}`;
      const reduced = /^giveaway winners reduced (\d+)->(\d+)/.exec(lc);
      if (reduced) return `Возврат с розыгрыша: победителей ${reduced[1]} → ${reduced[2]}`;
      if (lc.startsWith('giveaway cancelled')) return 'Возврат с отменённого розыгрыша';
      if (lc.startsWith('giveaway unused slots')) return 'Возврат за неразыгранные призы';
      if (lc.startsWith('giveaway prize unavailable')) return 'Возврат за невыданный приз';
      const batch = /^voucher batch x(\d+)/.exec(lc);
      if (batch) return `Покупка ваучеров: ${batch[1]} шт.`;
      if (lc.startsWith('voucher cancel refund')) return 'Возврат за отменённый ваучер';
      if (lc.includes('payout request')) return 'Запрос на вывод';
      if (lc.includes('payout hold')) return 'Резерв на вывод';
      if (lc.includes('payout auto-settled')) return 'Авто-вывод выполнен';
      if (lc.includes('payout via cryptobot')) return 'Вывод через CryptoBot';
      if (lc.includes('payout via ton')) return 'Вывод через TON';
      if (lc.includes('payout via xrocket')) return 'Вывод через xRocket';
      if (lc.includes('payout settle')) return 'Вывод выполнен';
      if (lc.includes('payout refund')) return 'Возврат вывода';
      if (lc.includes('partner markup')) return 'Партнёрская наценка';
      if (lc.includes('platform markup')) return 'Платформенная наценка';
      if (lc.includes('l1 referral bonus')) return 'Реферальный бонус L1';
      if (lc.includes('l2 referral bonus')) return 'Реферальный бонус L2';
      if (lc.includes('subpartner bonus')) return 'Бонус субпартнёра';
      if (lc.includes('mirror tree level')) return 'Доля с дерева зеркал';
      if (lc.includes('voucher')) return 'Партнёрский ваучер';
      if (lc.includes('promo')) return 'Промо-скидка';
      if (lc.includes('adaptgroup cost')) return 'Себестоимость VPN';
      if (lc.startsWith('vpn ')) return raw; // "VPN Pro 30d" — оставляем как есть
      return raw;
    };

    return rows.map((tx) => {
      const reason = tx.reason.toLowerCase();
      let kind: WalletTransaction['kind'];
      // Вывод клиента по рефералке для партнёра это поступление: он перевёл деньги сам,
      // а сумма из холда клиента пришла ему в доход. Без этой ветки её рисовало выводом.
      if (reason.startsWith('client_withdraw')) kind = tx.amount_minor >= 0 ? 'in' : 'out';
      else if (reason.startsWith('payout') || reason.includes('withdraw')) kind = 'withdraw';
      else if (reason.includes('referral')) kind = 'referral';
      else if (tx.amount_minor >= 0) kind = 'in';
      else kind = 'out';
      const displayMinor = tx.amount_display_minor ?? tx.amount_minor;
      const raw = (tx.description || '').trim();
      // Незнакомое описание латиницей заменяем подписью причины: машинный текст
      // партнёру ничего не говорит. Русское (ручная корректировка) оставляем.
      const translated = raw ? translateDescription(raw) : '';
      const description = !raw
        ? reasonLabels[reason] || reason
        : translated === raw && !/[а-яё]/i.test(raw) && !raw.toLowerCase().startsWith('vpn ') && reasonLabels[reason]
          ? reasonLabels[reason]
          : translated;
      return {
        id: tx.id,
        kind,
        amountRub: Math.round(displayMinor / 100),
        description,
        timestamp: tx.created_at,
        pocket: tx.pocket ?? 'earned',
      };
    });
  },

  // GET /api/partner/finance/payout-methods — описание методов вывода + fee'ы.
  payoutMethods: () =>
    tryRequest<
      Array<{
        method: string;
        label: string;
        fixed_fee_usd_minor: number;
        percent_fee_bp: number;
        destination_hint: string;
        min_payout_usd_minor: number;
        /** Минимум вывода в копейках рубля по текущему курсу, не меньше 100 ₽. Считает
         *  бэк тем же курсом, что и заявку: с ним сравнивается введённая сумма. */
        min_payout_display_minor: number;
        note?: string;
        /** Выплата через 2328: комиссия сети, точную сумму даёт `payoutQuote`. */
        fee_quote_required?: boolean;
        /** Можно указать комментарий (memo): USDT в сети TON на адрес биржи. */
        supports_memo?: boolean;
      }>
    >(
      'GET',
      '/api/partner/finance/payout-methods',
      // Мок-курс 100 ₽ за доллар, как у остальных заглушек: $10 это 1 000 ₽.
      () => [
        { method: 'cryptobot', label: 'CryptoBot (USDT)', fixed_fee_usd_minor: 0, percent_fee_bp: 300, destination_hint: '@username или CryptoBot ID', min_payout_usd_minor: 100, min_payout_display_minor: 10000 },
        { method: 'ton', label: 'TON (on-chain)', fixed_fee_usd_minor: 30, percent_fee_bp: 200, destination_hint: 'TON-адрес (EQ... / UQ...)', min_payout_usd_minor: 100, min_payout_display_minor: 10000 },
        { method: 'xrocket', label: 'xRocket (USDT)', fixed_fee_usd_minor: 0, percent_fee_bp: 300, destination_hint: 'Ваш Telegram, реквизиты не нужны', min_payout_usd_minor: 100, min_payout_display_minor: 10000, note: 'Перевод в Telegram-кошелёк @xRocket на ваш аккаунт. Если ни разу не открывали @xRocket, откройте его и нажмите «Старт».' },
        { method: 'sbp', label: 'СБП (по номеру телефона)', fixed_fee_usd_minor: 0, percent_fee_bp: 500, destination_hint: '+7XXXXXXXXXX, банк', min_payout_usd_minor: 1000, min_payout_display_minor: 100000, note: 'Перевод по СБП на номер телефона. На карту не переводим.' },
        { method: 'trc20', label: 'USDT TRC-20', fixed_fee_usd_minor: 100, percent_fee_bp: 100, destination_hint: 'TRC-20 адрес (TXXX...)', min_payout_usd_minor: 1000, min_payout_display_minor: 100000 },
        { method: 'bybit', label: 'Bybit (перевод по UID)', fixed_fee_usd_minor: 0, percent_fee_bp: 100, destination_hint: 'Bybit UID (только цифры)', min_payout_usd_minor: 100, min_payout_display_minor: 10000, note: 'Внутренний перевод USDT по UID аккаунта Bybit. Проверьте UID: перевод не по тому адресу вернуть нельзя.' },
        { method: 'bep20', label: 'USDT BEP-20 (BSC)', fixed_fee_usd_minor: 50, percent_fee_bp: 100, destination_hint: 'BEP-20 адрес (0x...)', min_payout_usd_minor: 1000, min_payout_display_minor: 100000 },
      ],
    ),

  // GET /api/partner/finance/payout-quote — точная комиссия вывода через 2328.
  // Суммы в копейках рубля. Мок: $2 сети по курсу 100.
  payoutQuote: (method: string, amountDisplayMinor: number, feeCharge: 'payment' | 'balance') =>
    tryRequest<{
      method: string;
      gross_display_minor: number;
      fee_display_minor: number;
      net_display_minor: number;
      min_display_minor: number;
    }>(
      'GET',
      `/api/partner/finance/payout-quote?method=${encodeURIComponent(method)}` +
        `&amount_display_minor=${amountDisplayMinor}&fee_charge=${feeCharge}`,
      () => {
        const fee = 20000;
        const gross = feeCharge === 'balance' ? amountDisplayMinor + fee : amountDisplayMinor;
        return {
          method,
          gross_display_minor: gross,
          fee_display_minor: fee,
          net_display_minor: gross - fee,
          min_display_minor: 100000,
        };
      },
    ),

  // GET/PUT /api/partner/finance/auto-payout — автовывод по порогу.
  // `available_methods` содержит только способы с реальным автоисполнением:
  // на остальных «автовывод» превратился бы в поток ручных заявок владельцу.
  autoPayout: () =>
    tryRequest<AutoPayoutSettings>('GET', '/api/partner/finance/auto-payout', () => ({
      enabled: false,
      threshold_display_minor: null,
      method: null,
      destination: null,
      pin_mode: null,
      min_interval_hours: 24,
      last_run_at: null,
      last_error: null,
      available_methods: ['cryptobot'],
      min_threshold_display_minor: 10000,
      auto_execution_cap_display_minor: 1600000,
    })),

  saveAutoPayout: (body: {
    enabled: boolean;
    threshold_display_minor: number | null;
    method: string | null;
    destination: string;
    pin_mode: string | null;
    min_interval_hours: number;
  }) =>
    tryRequest<AutoPayoutSettings>(
      'PUT',
      '/api/partner/finance/auto-payout',
      () => ({
        enabled: body.enabled,
        threshold_display_minor: body.threshold_display_minor,
        method: body.method,
        destination: body.destination,
        pin_mode: body.pin_mode,
        min_interval_hours: body.min_interval_hours,
        last_run_at: null,
        last_error: null,
        available_methods: ['cryptobot'],
        min_threshold_display_minor: 10000,
        auto_execution_cap_display_minor: 1600000,
      }),
      body,
    ),

  // POST /api/partner/finance/payouts — заявка на вывод.
  // fee_charge:
  //   'payment' — комиссия режется из заявки (input = gross, на руку = gross−fee).
  //   'balance' — комиссия добавляется к списанию (input = net, баланс минус gross=net+fee).
  requestWithdraw: (body: {
    amount_rub: number;
    method: string;
    destination: string;
    fee_charge?: 'payment' | 'balance';
    cryptobot_pin_mode?: 'self' | 'open' | 'username';
    memo?: string;
  }) =>
    tryRequest<{
      id: string;
      status: string;
      auto_executed?: boolean;
      external_ref?: string | null;
      auto_error?: string | null;
      transfer_state?: 'sending' | 'sent' | 'unknown' | null;
    }>(
      'POST',
      '/api/partner/finance/payouts',
      () => ({ id: '', status: 'failed' }),
      {
        amount_display_minor: body.amount_rub * 100,
        method: body.method,
        destination: body.destination,
        note: null,
        fee_charge: body.fee_charge ?? 'payment',
        // Без этого поля бэк считал заявку выводом «себе» и привязывал чек к
        // Telegram заказчика, игнорируя введённый логин получателя.
        ...(body.cryptobot_pin_mode ? { cryptobot_pin_mode: body.cryptobot_pin_mode } : {}),
        ...(body.memo ? { memo: body.memo } : {}),
      },
    ),

  partnerPayouts: () =>
    tryRequest<
      Array<{
        id: string;
        amount_usd_minor: number;
        fee_usd_minor?: number;
        net_usd_minor?: number;
        method: string;
        destination: string;
        status: string;
        note: string | null;
        created_at: string;
        auto_executed?: boolean;
        external_ref?: string | null;
        auto_error?: string | null;
        /** Перевод в пути (`sending`/`sent`) или проверяем, дошёл ли (`unknown`). */
        transfer_state?: 'sending' | 'sent' | 'unknown' | null;
      }>
    >('GET', '/api/partner/finance/payouts', () => []),

  // GET /api/partner/referrals/stats — клиентская реф-программа партнёра.
  // Бэк отдаёт {total_referred, locked_referred, total_bonus_paid_minor, currency},
  // но UI ждёт {link, invited, active, earnedRub, l1Percent, l2Percent}. Мапим тут.
  referralStats: async (): Promise<ReferralStats> => {
    type BackendStats = {
      total_referred: number;
      locked_referred: number;
      total_bonus_paid_minor: number;
      currency: string;
      enabled: boolean;
      l1_percent: number;
      l2_percent: number;
      withdraw_enabled?: boolean;
      withdraw_min_display_minor?: number;
      withdraw_methods?: string[];
      withdraw_currency?: string;
      withdraw_pending?: number;
      withdraw_available?: boolean;
      withdraw_platform_methods?: string[];
      withdraw_auto_enabled?: boolean;
      withdraw_auto_max_display_minor?: number | null;
    };
    const [stats, me] = await Promise.all([
      tryRequest<BackendStats>('GET', '/api/partner/referrals/stats', () => ({
        total_referred: 0,
        locked_referred: 0,
        total_bonus_paid_minor: 0,
        currency: 'USD',
        enabled: false,
        l1_percent: 15,
        l2_percent: 5,
        withdraw_enabled: false,
        withdraw_min_display_minor: 10000,
        withdraw_methods: ['card', 'sbp', 'usdt_trc20'],
        withdraw_currency: 'RUB',
        withdraw_pending: 0,
        withdraw_available: true,
      })),
      api.me().catch(() => null),
    ]);
    const rate = await usdToDisplayRate();
    const botUsername = me?.franchiseBotUsername;
    const partnerId = auth.partnerId;
    const link = botUsername && partnerId
      ? `https://t.me/${botUsername}?start=ref_${partnerId}`
      : '';
    return {
      link,
      invited: stats.total_referred ?? 0,
      active: stats.locked_referred ?? 0,
      // total_bonus_paid_minor — центы USD, переводим в рубли по курсу. До 02.10.2026
      // здесь стояли целые доллары с подписью «₽»: сумма была меньше в ~80 раз.
      earnedRub: Math.round(((stats.total_bonus_paid_minor ?? 0) * rate) / 100),
      enabled: stats.enabled ?? false,
      // Реф-проценты приходят с бэка (/api/partner/referrals/stats → l1_percent/l2_percent —
      // эффективная ставка партнёра). Fallback 15/5 — на случай mock/старого ответа без полей.
      l1Percent: stats.l1_percent ?? 15,
      l2Percent: stats.l2_percent ?? 5,
      withdrawEnabled: Boolean(stats.withdraw_enabled),
      withdrawMinDisplayMinor: stats.withdraw_min_display_minor ?? 10000,
      withdrawMethods: (stats.withdraw_methods ?? []) as ClientWithdrawalMethod[],
      withdrawCurrency: stats.withdraw_currency ?? 'RUB',
      withdrawPending: stats.withdraw_pending ?? 0,
      // Старый ответ без поля: считаем доступным, как и бэк по умолчанию.
      withdrawAvailable: stats.withdraw_available ?? true,
      withdrawPlatformMethods: (stats.withdraw_platform_methods ?? []) as ClientWithdrawalMethod[],
      withdrawAutoEnabled: Boolean(stats.withdraw_auto_enabled),
      withdrawAutoMaxDisplayMinor: stats.withdraw_auto_max_display_minor ?? null,
    };
  },

  // PUT /api/partner/referrals/settings — обновить реф-настройки партнёра.
  // Партнёр платит бонусы из своей маржи (retail − platform). Сумма L1+L2 ≤ 100%.
  // Поля вывода необязательные: не переданное бэк не трогает. Зеркалу их не шлём вовсе.
  updateReferralSettings: (input: {
    enabled: boolean;
    l1Percent: number;
    l2Percent: number;
    withdrawEnabled?: boolean;
    withdrawMinDisplayMinor?: number;
    withdrawMethods?: ClientWithdrawalMethod[];
    withdrawAutoEnabled?: boolean;
    withdrawAutoMaxDisplayMinor?: number;
  }): Promise<void> =>
    request<void>('PUT', '/api/partner/referrals/settings', {
      enabled: input.enabled,
      l1_percent: input.l1Percent,
      l2_percent: input.l2Percent,
      ...(input.withdrawEnabled !== undefined ? { withdraw_enabled: input.withdrawEnabled } : {}),
      ...(input.withdrawMinDisplayMinor !== undefined
        ? { withdraw_min_display_minor: input.withdrawMinDisplayMinor }
        : {}),
      ...(input.withdrawMethods !== undefined ? { withdraw_methods: input.withdrawMethods } : {}),
      ...(input.withdrawAutoEnabled !== undefined ? { withdraw_auto_enabled: input.withdrawAutoEnabled } : {}),
      ...(input.withdrawAutoMaxDisplayMinor !== undefined
        ? { withdraw_auto_max_display_minor: input.withdrawAutoMaxDisplayMinor }
        : {}),
    }).catch((e: unknown) => {
      // Поля вывода меняет только владелец: делегату бэк отвечает 403 с reason
      // "delegate". Это не повод выкидывать из кабинета, только понятный текст.
      if (e instanceof ApiError && e.status === 403 && isDelegateForbidden(e.body)) {
        e.message = 'Настройки вывода меняет только владелец кабинета';
      }
      throw e;
    }),

  // GET /api/partner/client-withdrawals — заявки клиентов на вывод реферального
  // заработка (идея #162), новые сверху. Деньги партнёр переводит сам, здесь он
  // отмечает итог: «Выплачено» возвращает сумму ему в доход, «Отклонить» клиенту.
  // `status=pending` отдаёт все открытые, вместе с взятыми в работу («Перевожу»).
  // Курсор непрозрачный: next_cursor передаём обратно как есть.
  clientWithdrawals: async (params: ClientWithdrawalsQuery = {}): Promise<ClientWithdrawalsPage> => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.botId) qs.set('bot_id', params.botId);
    if (params.cursor) qs.set('cursor', params.cursor);
    qs.set('limit', String(params.limit ?? 50));
    const [page, rate] = await Promise.all([
      tryRequest<{ items: BackendClientWithdrawal[]; next_cursor: string | null }>(
        'GET',
        `/api/partner/client-withdrawals?${qs}`,
        () => ({ items: [], next_cursor: null }),
      ),
      usdToDisplayRate(),
    ]);
    return {
      items: page.items.map((w) => toClientWithdrawal(w, rate)),
      nextCursor: page.next_cursor,
    };
  },

  clientWithdrawalCounts: () =>
    tryRequest<ClientWithdrawalCounts>('GET', '/api/partner/client-withdrawals/counts', () => ({
      pending: 0,
      processing: 0,
      paid: 0,
      rejected: 0,
      cancelled: 0,
    })),

  // «Перевожу»: заявка уходит в работу, и клиент её больше не отменит. Жать ДО
  // перевода, иначе клиент мог отменить заявку, пока деньги уже в пути. Только из
  // pending, иначе 409 app.referral_withdraw.closed. Отдаёт заявку в новом статусе.
  takeClientWithdrawal: async (id: string): Promise<ClientWithdrawal | null> => {
    const [w, rate] = await Promise.all([
      tryRequest<BackendClientWithdrawal | null>(
        'POST',
        `/api/partner/client-withdrawals/${encodeURIComponent(id)}/take`,
        () => null,
      ),
      usdToDisplayRate(),
    ]);
    return w ? toClientWithdrawal(w, rate) : null;
  },

  // «Выплатить через платформу» (заявки #183, #186): GRAM или чек CryptoBot платформа
  // переводит сама, комиссия из суммы клиента. Выше лимита заявка ждёт одобрения
  // платформы (`ownerApproval: required`). Голый `request`: 409 тут значит «уже решено»
  // или «способ сейчас не платится», а не «бот выключен».
  sendClientWithdrawalViaPlatform: async (id: string): Promise<ClientWithdrawal> => {
    const [w, rate] = await Promise.all([
      request<BackendClientWithdrawal>(
        'POST',
        `/api/partner/client-withdrawals/${encodeURIComponent(id)}/platform`,
      ),
      usdToDisplayRate(),
    ]);
    return toClientWithdrawal(w, rate);
  },

  // Делегатам можно: деньги при «Выплачено» идут не от партнёра, а к нему.
  // «Выплачено» и «Отклонить» работают и из pending, и из processing.
  markClientWithdrawalPaid: async (id: string): Promise<void> => {
    await tryRequest<unknown>(
      'POST',
      `/api/partner/client-withdrawals/${encodeURIComponent(id)}/paid`,
      () => null,
    );
  },

  rejectClientWithdrawal: async (id: string, reason: string): Promise<void> => {
    await tryRequest<unknown>(
      'POST',
      `/api/partner/client-withdrawals/${encodeURIComponent(id)}/reject`,
      () => null,
      { reason },
    );
  },

  // GET/PUT /api/partner/trial — пробники. Настройка партнёрская: одна на все его
  // боты. У «На срок» и «Навсегда» свои тумблеры (`partners.trial_enabled` и
  // `partners.lifetime_trial_enabled`, заявка #160).
  trialSettings: async (): Promise<TrialSettings> =>
    toTrialSettings(
      await request<BackendTrialSettings>('GET', '/api/partner/trial'),
    ),

  updateTrialSettings: async (
    body: { enabled?: boolean; lifetime_enabled?: boolean; whitelist_auto?: boolean },
  ): Promise<TrialSettings> =>
    toTrialSettings(
      await request<BackendTrialSettings>('PUT', '/api/partner/trial', body),
    ),

  // GET /api/partner/referrals/list — список приведённых клиентов.
  // Бэк отдаёт {client_id, tg_user_id, tg_username, referrer_id, locked},
  // но UI ждёт {id, firstName, level, joinedAt, earnedRub, status}. Мапим.
  referralList: async (): Promise<ReferralEntry[]> => {
    type BackendEntry = {
      client_id: string;
      tg_user_id: number;
      tg_username: string | null;
      referrer_id: string | null;
      locked: boolean;
      joined_at: string;
      bonus_generated_minor: number;
    };
    const rows = await tryRequest<BackendEntry[]>(
      'GET',
      '/api/partner/referrals/list',
      () => [],
    );
    // Раньше здесь стояли заглушки: у всех «+0 ₽» и сегодняшняя дата. Бэк эти поля
    // теперь отдаёт, выдумывать нечего.
    const rate = await usdToDisplayRate();
    return rows.map((r) => ({
      id: r.client_id,
      firstName: r.tg_username || `tg ${r.tg_user_id}`,
      level: 1 as const,
      joinedAt: r.joined_at,
      earnedRub: Math.round(((r.bonus_generated_minor ?? 0) * rate) / 100),
      status: r.locked ? 'active' : 'churned',
    }));
  },

  // GET /api/partner/referrals/top — кто из клиентов приносит больше денег.
  // botId — срез по одному боту; без него топ по всем ботам партнёра.
  referralTop: async (botId?: string): Promise<TopReferrer[]> => {
    type BackendTop = {
      client_id: string;
      tg_user_id: number;
      tg_username: string | null;
      referred_count: number;
      bonus_earned_minor: number;
    };
    const qs = botId ? `?bot_id=${encodeURIComponent(botId)}` : '';
    const rows = await tryRequest<BackendTop[]>(
      'GET',
      `/api/partner/referrals/top${qs}`,
      () => [],
    );
    const rate = await usdToDisplayRate();
    return rows.map((r) => ({
      id: r.client_id,
      name: r.tg_username ? `@${r.tg_username}` : `tg ${r.tg_user_id}`,
      referredCount: r.referred_count,
      earnedRub: Math.round(((r.bonus_earned_minor ?? 0) * rate) / 100),
    }));
  },

  // Рассылки партнёра по своим клиентам. Backend audience: all_clients |
  // active_clients | expired_clients. Status: queued → running → sent | failed.
  broadcasts: () =>
    tryRequest<
      Array<{
        id: string;
        title: string;
        body: string;
        audience: string;
        status: string;
        /** Анимация при доставке и признак, что Telegram её отклонил. */
        message_effect_id: string | null;
        protect_content: boolean;
        effect_dropped: boolean;
        sent_count: number;
        failed_count: number;
        total_count: number;
        message_preview: string | null;
        started_at: string | null;
        finished_at: string | null;
        created_at: string;
        recurrence: string | null;
        time_of_day: string | null;
        weekday: number | null;
        is_recurring_active: boolean;
        next_fire_at: string | null;
        fire_count: number;
      }>
    >('GET', '/api/partner/broadcasts', () => []),

  /** Остановить рассылку и снять расписание. Работает и на уже отправленной
   *  повторяющейся — у неё между запусками status='sent'. */
  cancelBroadcast: (broadcastId: string) =>
    request<{ id: string; status: string; is_recurring_active: boolean }>(
      'POST',
      `/api/partner/broadcasts/${broadcastId}/cancel`,
    ),

  /** Сколько клиентов получат рассылку: для подтверждения перед отправкой. */
  broadcastAudienceSize: (audience: string, botId: string | null) =>
    request<{ count: number }>(
      'GET',
      `/api/partner/broadcasts/audience-size?audience=${encodeURIComponent(audience)}${
        botId ? `&bot_id=${encodeURIComponent(botId)}` : ''
      }`,
    ),

  createBroadcast: (body: {
    body: string;
    audience: string;
    // Block A.8: scheduled / recurring
    recurrence?: 'once' | 'daily' | 'weekly' | null;
    scheduled_at?: string | null;
    time_of_day?: string | null;
    weekday?: number | null;
    // Block D.3: защита от форварда
    protect_content?: boolean;
    // Block D.4: emoji-реакции под сообщением (до 5) — LEGACY
    reaction_emojis?: string[] | null;
    // Native TG animated effect (Bot API 7.2+) — анимация при доставке
    message_effect_id?: string | null;
    // Per-bot scope. null = во все боты партнёра.
    bot_id?: string | null;
    // Ф3/Ф4/Ф7: скорость, кнопки, медиа
    rate_per_second?: number | null;
    inline_buttons?: Array<Array<{ text: string; url?: string; kind?: 'url' | 'action'; action?: string; style?: 'primary' | 'success' | 'danger'; icon?: string; icon_id?: string }>> | null;
    media_type?: string | null;
    media_url?: string | null;
    // Заявка #203: ссылки кнопок через наш редирект, чтобы считать переходы.
    track_links?: boolean;
  }) =>
    tryRequest<{
      id: string;
      title: string;
      body: string;
      audience: string;
      status: string;
      sent_count: number;
      failed_count: number;
      total_count: number;
      message_preview: string | null;
      started_at: string | null;
      finished_at: string | null;
      created_at: string;
    }>(
      'POST',
      '/api/partner/broadcasts',
      () => ({
        id: `b_${Date.now()}`,
        title: body.body.split('\n')[0].slice(0, 60) || 'Рассылка',
        body: body.body,
        audience: body.audience,
        status: 'queued',
        sent_count: 0,
        failed_count: 0,
        total_count: 0,
        message_preview: null,
        started_at: null,
        finished_at: null,
        created_at: new Date().toISOString(),
      }),
      body,
    ),

  // Block D.4: счётчики emoji-реакций для broadcast'а.
  broadcastReactions: (broadcastId: string) =>
    tryRequest<Array<{ emoji: string; count: number }>>(
      'GET',
      `/api/partner/broadcasts/${broadcastId}/reactions`,
      () => [],
    ),

  // Ф5: живой прогресс рассылки (счётчики + скорость + хвост последних доставок).
  // GET /api/partner/broadcasts/{id}/results — «Результат рассылки» (заявка #203).
  // Без mock-фолбэка: выдуманные нажатия и покупки хуже честной ошибки.
  broadcastResults: (broadcastId: string) =>
    request<BroadcastResults>('GET', `/api/partner/broadcasts/${broadcastId}/results`),

  broadcastLive: (broadcastId: string) =>
    tryRequest<{
      status: string;
      sent: number;
      failed: number;
      total: number;
      rate_per_second: number | null;
      computed_speed: number | null;
      eta_seconds: number | null;
      tail: Array<{ tg_user_id: number; username: string | null; ok: boolean; ts: number }>;
      log_available?: boolean;
      effect_dropped?: boolean;
    }>('GET', `/api/partner/broadcasts/${broadcastId}/live`, () => ({
      status: 'sent',
      sent: 0,
      failed: 0,
      total: 0,
      rate_per_second: null,
      computed_speed: null,
      eta_seconds: null,
      tail: [],
    })),

  // Ф6: тест-отправка черновика себе в ЛС через дочерний бот. Бросает ApiError(409)
  // с detail.error='bot_cant_message' если бот не может написать партнёру первым.
  testBroadcast: async (draft: {
    body: string;
    bot_id?: string | null;
    media_type?: string | null;
    media_url?: string | null;
    inline_buttons?: Array<Array<{ text: string; url?: string; kind?: 'url' | 'action'; action?: string; style?: 'primary' | 'success' | 'danger'; icon?: string; icon_id?: string }>> | null;
    protect_content?: boolean;
    message_effect_id?: string | null;
  }): Promise<{ sent: boolean; bot_username: string; effect_dropped?: boolean; message?: string | null }> =>
    request('POST', '/api/partner/broadcasts/test', draft),

  // Рассылка из сообщения: забрать заготовку (текст+медиа), собранную ботом. Токен
  // приходит в ?bcastDraft. 404 если истёк/чужой.
  getBroadcastDraft: (token: string) =>
    request<{
      body: string;
      media_type: string | null;
      media_url: string | null;
      inline_buttons: { text: string; url?: string; kind?: 'url' | 'action'; action?: string; style?: 'primary' | 'success' | 'danger'; icon?: string; icon_id?: string }[][] | null;
      bot_id: string | null;
    }>('GET', `/api/partner/broadcasts/draft/${encodeURIComponent(token)}`),

  // Ф7: загрузка медиа рассылки (multipart). Возвращает {media_type, media_url}.
  uploadBroadcastMedia: async (file: File): Promise<{ media_type: string; media_url: string }> => {
    if (!API_BASE) {
      throw new ApiError(0, null, 'VITE_API_BASE not configured — media uploads need real backend');
    }
    const { token } = await authenticate();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/api/partner/broadcasts/media`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new ApiError(res.status, err, `uploadBroadcastMedia → ${res.status}`);
    }
    return res.json();
  },

  // Block 5: список переменных-шаблонов для broadcast composer'а.
  // Реестр «действий» для кнопок рассылки (тарифы, профиль, промокод…). Живёт на
  // бэке, чтобы список разделов не дублировался в копиях редактора.
  broadcastButtonActions: () =>
    tryRequest<Array<{ key: string; kind: 'webapp' | 'bot'; label: string }>>(
      'GET',
      '/api/partner/broadcasts/button-actions',
      () => [],
    ),

  broadcastTemplateVars: () =>
    tryRequest<
      Array<{
        name: string;
        description: string;
        example: string;
        requires_subscription?: boolean;
      }>
    >(
      'GET',
      '/api/partner/broadcasts/template-vars',
      () => [
        { name: 'username', description: 'Имя клиента (или «друг»)', example: '@alice' },
        { name: 'bot_name', description: 'Название вашего бота', example: 'HexVPN' },
        { name: 'bot_link', description: 'Ссылка на бота', example: 'https://t.me/hexvpnbot' },
        { name: 'date', description: 'Сегодняшняя дата (МСК)', example: '01.07.2026' },
        { name: 'time', description: 'Текущее время (МСК)', example: '14:30' },
        { name: 'weekday', description: 'День недели (МСК)', example: 'вторник' },
        { name: 'plan_name', description: 'Подписка клиента', example: 'Pro 30 дней', requires_subscription: true },
        { name: 'expire_date', description: 'Дата окончания подписки', example: '25.06.2026', requires_subscription: true },
        { name: 'days_left', description: 'Дней до конца подписки', example: '5', requires_subscription: true },
      ],
    ),

  // GET /api/partner/webhooks. Backend → {id, source, event, status, received_at, attempts}.
  // UI ждёт {id, source, event, status, receivedAt, attempts, rawPreview}.
  // Backend status: received/processed/failed → UI: ok/failed/retrying.
  webhooks: async (): Promise<WebhookEvent[]> => {
    type BackendWebhook = {
      id: string;
      source: string;
      event: string;
      status: string;
      received_at: string;
      attempts: number;
      raw_preview: string | null;
      error: string | null;
    };
    const rows = await tryRequest<BackendWebhook[]>('GET', '/api/partner/webhooks', () => []);
    return rows.map((e) => {
      const status: WebhookEvent['status'] =
        e.status === 'processed' || e.status === 'ok'
          ? 'ok'
          : e.status === 'failed'
          ? 'failed'
          : 'retrying';
      const source = (['adaptgroup', 'cryptobot', 'stars', 'robokassa'] as const).includes(
        e.source as never,
      )
        ? (e.source as WebhookEvent['source'])
        : 'adaptgroup';
      return {
        id: e.id,
        source,
        event: e.event,
        status,
        receivedAt: e.received_at,
        attempts: e.attempts,
        rawPreview: e.raw_preview ?? '',
      };
    });
  },

  // Phase 4 follow-up: POST /api/partner/webhooks/{id}/retry — реальный эндпоинт.
  retryWebhook: (id: string) =>
    tryRequest<{ ok: boolean }>(
      'POST',
      `/api/partner/webhooks/${id}/retry`,
      () => ({ ok: true }),
    ),

  // ---- Vouchers ----
  // ---------- Свой VPN (дерево зеркал) ----------
  mirrors: () =>
    tryRequest<MirrorsOverview>('GET', '/api/partner/mirrors', () => ({
      enabled: false,
      max_depth: 0,
      total_earned_usd_minor: 0,
      rows: [],
      bots: [],
    })),

  // ---------- Розыгрыши ----------
  giveaways: (botId?: string) =>
    tryRequest<Giveaway[]>(
      'GET',
      `/api/partner/giveaways${botId ? `?bot_id=${botId}` : ''}`,
      () => [],
    ),

  createGiveaway: (body: {
    bot_id: string;
    title: string;
    description: string | null;
    ends_at: string;
    winners_count: number;
    prize_plan_uuid: string;
  }) => request<Giveaway>('POST', '/api/partner/giveaways', body),

  updateGiveaway: (
    id: string,
    body: Partial<{
      title: string;
      description: string | null;
      ends_at: string;
      winners_count: number;
      prize_plan_uuid: string;
    }>,
  ) => request<Giveaway>('PATCH', `/api/partner/giveaways/${id}`, body),

  // Каналы валидируются на бэке (бот обязан быть админом) — 422 с человекочитаемым текстом.
  setGiveawayTasks: (id: string, tasks: GiveawayTaskInput[]) =>
    request<Giveaway>('PUT', `/api/partner/giveaways/${id}/tasks`, { tasks }),

  launchGiveaway: (id: string) =>
    request<Giveaway>('POST', `/api/partner/giveaways/${id}/launch`),

  /** Сколько спишется при запуске (или при смене числа победителей) и хватает ли. */
  giveawayEscrowPreview: (id: string, winnersCount?: number) =>
    request<GiveawayEscrowPreview>(
      'GET',
      `/api/partner/giveaways/${id}/escrow-preview${winnersCount ? `?winners_count=${winnersCount}` : ''}`,
    ),

  cancelGiveaway: (id: string) =>
    request<Giveaway>('POST', `/api/partner/giveaways/${id}/cancel`),

  deleteGiveaway: (id: string) =>
    request<void>('DELETE', `/api/partner/giveaways/${id}`),

  /** Собрать черновик рассылки о розыгрыше. Ничего не шлёт: возвращает токен,
   *  с которым composer открывается предзаполненным. */
  announceGiveaway: (id: string) =>
    request<{ draft_token: string }>('POST', `/api/partner/giveaways/${id}/announce`),

  // ---------- Переезд бота с другой франшизы ----------
  // Только владелец кабинета: делегату бэк отвечает 403. Имена методов те же, что на
  // сайте (check-parity.mjs сверяет их по именам).
  botImports: () => tryRequest<BotImport[]>('GET', '/api/partner/bot-imports', () => []),

  botImport: (id: string) => request<BotImport>('GET', `/api/partner/bot-imports/${id}`),

  /** Начать переезд. С `token` или `bot_id` бот подключается сразу, без них позже. */
  createBotImport: (body: { source_name?: string | null; token?: string; bot_id?: string }) =>
    request<BotImport>('POST', '/api/partner/bot-imports', body),

  /** Подключить бота к начатому переезду: новым токеном или уже подключённого. */
  connectBotImportBot: (id: string, body: { token?: string; bot_id?: string }) =>
    request<BotImport>('POST', `/api/partner/bot-imports/${id}/bot`, body),

  /** Выгрузка базы (multipart). `dryRun` только показывает, как поняли колонки, и
   *  ничего не сохраняет; сохраняет повторная отправка того же файла с разметкой. */
  uploadBotImportRows: async (
    id: string,
    file: File,
    opts: { dryRun: boolean; mapping?: BotImportMapping | null },
  ): Promise<BotImportRowsPreview> => {
    if (!API_BASE) {
      throw new ApiError(0, null, 'VITE_API_BASE not configured: bot import upload needs the backend');
    }
    const { token } = await authenticate();
    const fd = new FormData();
    fd.append('file', file);
    if (opts.mapping) fd.append('mapping', JSON.stringify(opts.mapping));
    const res = await fetch(
      `${API_BASE}/api/partner/bot-imports/${id}/rows?dry_run=${opts.dryRun}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new ApiError(res.status, err, `uploadBotImportRows → ${res.status}`);
    }
    return res.json();
  },

  /** «Переносить подписки?»: да ждёт решения платформы, нет закрывает вопрос. */
  botImportTransfer: (id: string, want: boolean) =>
    request<BotImport>('POST', `/api/partner/bot-imports/${id}/transfer`, { want }),

  /** Сколько сейчас стоит перенос и хватает ли баланса. Сумма со временем только падает. */
  botImportChargePreview: (id: string) =>
    request<BotImportChargePreview>('GET', `/api/partner/bot-imports/${id}/charge-preview`),

  /** Оплатить перенос с баланса. Не хватает: 409 `app.bot_import.insufficient_balance`. */
  payBotImport: (id: string) => request<BotImport>('POST', `/api/partner/bot-imports/${id}/pay`),

  /** Черновик рассылки «мы переехали» на всю базу. Ничего не шлёт, как и анонс розыгрыша. */
  botImportBroadcastDraft: (id: string) =>
    request<{ draft_token: string }>('POST', `/api/partner/bot-imports/${id}/broadcast-draft`),

  // Порог скидки, который покрывает наценка ботов, и деньги на доплату (заявка #211).
  discountCoverage: () =>
    request<DiscountCoverage>('GET', '/api/partner/vouchers/discount-coverage'),
  voucherPlans: () =>
    tryRequest<{
      plans: Array<{
        plan_uuid: string;
        name: string;
        devices: number;
        days: number;
        cost_per_voucher_display_minor: number;
        display_currency: string;
        /** «Навсегда»: `days` у него технический (36500), срок не показываем. */
        is_lifetime?: boolean;
      }>;
    }>('GET', '/api/partner/vouchers/plans', () => ({ plans: [] })),

  cancelVoucher: (voucherId: string) =>
    tryRequest<{
      voucher: { id: string; code: string; status: string };
      refund_display_minor: number;
      display_currency: string;
    }>(
      'POST',
      `/api/partner/vouchers/${voucherId}/cancel`,
      () => ({
        voucher: { id: voucherId, code: '', status: 'cancelled' },
        refund_display_minor: 0,
        display_currency: 'RUB',
      }),
    ),

  vouchers: (
    status?: 'available' | 'redeemed' | 'expired' | 'cancelled',
    batchId?: string,
  ) =>
    tryRequest<{
      items: Array<{
        id: string;
        code: string;
        batch_id: string;
        kind?: 'free' | 'discount';
        discount_percent?: number | null;
        plan_uuid: string | null;
        plan_name: string;
        cost_display_minor: number;
        display_currency: string;
        status: string;
        created_at: string;
        expires_at: string;
        redeemed_at: string | null;
        redeemed_by_client_id: string | null;
        max_activations?: number;
        used_count?: number;
        activations_left?: number;
        /** Сколько ещё заморожено под этот код, в валюте витрины. */
        held_display_minor?: number;
      }>;
    }>(
      'GET',
      `/api/partner/vouchers?${new URLSearchParams({
        ...(status ? { status } : {}),
        ...(batchId ? { batch_id: batchId } : {}),
      }).toString()}`,
      () => ({ items: [] }),
    ),

  /** `activations` — сколько подписок выдаёт один код. Деньги под все замораживаются
   *  сразу при выпуске, поэтому счёт и списание считаются по count × activations. */
  purchaseVouchers: (planUuid: string, count: number, customName?: string, activations = 1) =>
    tryRequest<{
      batch_id: string;
      codes: string[];
      total_cost_display_minor: number;
      display_currency: string;
    }>(
      'POST',
      '/api/partner/vouchers/purchase',
      () => ({ batch_id: '', codes: [], total_cost_display_minor: 0, display_currency: 'RUB' }),
      {
        plan_uuid: planUuid,
        count,
        activations,
        custom_name: customName?.trim() || undefined,
      },
    ),

  // ---------- Свои кнопки партнёра в меню бота ----------
  getCustomButtons: (botId: string) =>
    tryRequest<CustomButtons>(
      'GET',
      `/api/partner/bots/${botId}/custom-buttons`,
      () => ({ sections: {}, max_per_section: 3, available_actions: [] }),
    ),
  updateCustomButtons: (botId: string, sections: CustomButtons['sections']) =>
    request<CustomButtons>('PUT', `/api/partner/bots/${botId}/custom-buttons`, {
      sections,
    }),

  // ---------- Партнёрские счета (оплата ваучеров и призов платёжкой) ----------
  // Клиента у такого платежа нет: платит сам партнёр. Деньги ложатся в невыводимый
  // карман и тут же тратятся на то, за что выставлен счёт.
  partnerPaymentProviders: () =>
    tryRequest<{ providers: string[] }>(
      'GET',
      '/api/partner/payments/providers',
      () => ({ providers: [] }),
    ),

  // Два кармана: заработанное (выводится) и оплаченное (только на ваучеры/призы).
  partnerFunds: () =>
    tryRequest<{
      balance_usd_minor: number;
      paid_usd_minor: number;
      spendable_usd_minor: number;
    }>('GET', '/api/partner/payments/funds', () => ({
      balance_usd_minor: 0,
      paid_usd_minor: 0,
      spendable_usd_minor: 0,
    })),

  partnerInvoice: (body: {
    purpose: 'voucher_batch' | 'giveaway_escrow' | 'topup' | 'bot_import';
    provider: string;
    plan_uuid?: string;
    count?: number;
    /** voucher_batch: сколько активаций у каждого кода. Счёт покрывает все сразу. */
    activations?: number;
    custom_name?: string | null;
    giveaway_id?: string;
    /** giveaway_escrow у запущенного: новое число победителей, счёт на разницу. */
    winners_count?: number;
    /** topup: сумма пополнения в центах USD. */
    amount_usd_minor?: number;
    /** bot_import: переезд, за перенос подписок которого платит партнёр. */
    bot_import_id?: string;
  }) =>
    request<{
      payment_id: string;
      provider: string;
      invoice_url: string;
      expires_at: string | null;
      amount_display_minor: number;
      gross_amount_minor: number;
      currency: string;
    }>('POST', '/api/partner/payments/invoice', body),

  partnerPaymentStatus: (paymentId: string) =>
    request<{ payment_id: string; status: string }>(
      'GET',
      `/api/partner/payments/${paymentId}/status`,
    ),

  // count — сколько РАЗНЫХ кодов; activations — сколько раз можно активировать
  // каждый. Один код на 30 активаций это count=1, activations=30.
  issueDiscountVouchers: (
    count: number,
    discountPercent: number,
    opts?: { activations?: number; customCode?: string | null },
  ) =>
    request<{
      batch_id: string;
      codes: string[];
      total_cost_display_minor: number;
      display_currency: string;
    }>('POST', '/api/partner/vouchers/discount', {
      count,
      discount_percent: discountPercent,
      activations: opts?.activations ?? 1,
      custom_code: opts?.customCode || null,
    }),

  // ---------- UTM-кампании (реклама) ----------
  // Деньги в ответах — USD-cents + fx_rate_to_display для конверсии в ₽ на фронте.

  campaigns: (includeArchived = false) =>
    tryRequest<CampaignListResponse>(
      'GET',
      `/api/partner/campaigns?include_archived=${includeArchived}`,
      () => ({ items: [], fx_rate_to_display: 100, display_currency: 'RUB' }),
    ),

  createCampaign: (name: string, botId: string) =>
    request<CampaignItem>('POST', '/api/partner/campaigns', {
      name: name.trim(),
      bot_id: botId,
    }),

  updateCampaign: (
    campaignId: string,
    body: { name?: string; status?: 'active' | 'archived'; note?: string | null },
  ) =>
    request<CampaignItem>('PATCH', `/api/partner/campaigns/${campaignId}`, body),

  campaignStats: (campaignId: string, periodDays = 30) =>
    tryRequest<CampaignStatsResponse>(
      'GET',
      `/api/partner/campaigns/${campaignId}/stats?period_days=${periodDays}`,
      () => ({
        campaign: EMPTY_CAMPAIGN,
        daily: [],
        plans: [],
        spend_entries: [],
        fx_rate_to_display: 100,
        display_currency: 'RUB',
      }),
    ),

  addCampaignSpend: (
    campaignId: string,
    body: { spent_on: string; amount_minor: number; currency: 'RUB' | 'USD'; comment?: string },
  ) =>
    request<CampaignSpendEntry>('POST', `/api/partner/campaigns/${campaignId}/spend`, body),

  deleteCampaignSpend: (campaignId: string, entryId: string) =>
    request<{ ok: boolean }>('DELETE', `/api/partner/campaigns/${campaignId}/spend/${entryId}`),

  utmTraffic: (limit = 100) =>
    tryRequest<UtmTrafficResponse>(
      'GET',
      `/api/partner/campaigns/utm-traffic?limit=${limit}`,
      () => ({ items: [], fx_rate_to_display: 100, display_currency: 'RUB' }),
    ),
};

export { ApiError };

/** Тема клиентского мини-аппа (`application/bots/miniapp_theme.py`). */
export type MiniappTheme = 'graphite' | 'schema' | 'cloud' | 'classic';

// ─── Шаблоны оформления бота ───────────────────────────────────────────────

export type BotTemplatePart = 'banners' | 'icons' | 'miniapp' | 'site';

export interface BotTemplatePartState {
  available: boolean;
  /** `media_disabled` / `color_not_ready` — недоступна; `premium_off` — доступна, но
   *  иконки не покажутся без премиум-эмодзи у бота. */
  reason: string | null;
}

export interface BotTemplateVariant {
  id: string;
  label: string;
  swatch: string;
  icon_color: string | null;
  primary_color: string;
  parts: Record<BotTemplatePart, BotTemplatePartState>;
}

export interface BotTemplate {
  id: string;
  title: string;
  description: string;
  miniapp_theme: MiniappTheme;
  site_theme: 'map' | 'pixel' | 'block';
  titles: Record<string, [string, string | null]>;
  variants: BotTemplateVariant[];
  max_name_chars: number;
}

export interface BotTemplatesResponse {
  templates: BotTemplate[];
  sections: string[];
  default_name: string;
  premium_emoji_enabled: boolean;
  last_application: {
    id: string;
    template: string;
    variant: string;
    parts: BotTemplatePart[];
    name: string;
    created_at: string;
  } | null;
  name_outdated: boolean;
}

export interface BotTemplateApplyBody {
  template: string;
  variant: string;
  parts: BotTemplatePart[];
  name: string;
  enable_premium_emoji: boolean;
}

/** Сезонный декор мини-аппа (`application/bots/miniapp_season.py`). */
export type MiniappSeason = 'autumn' | 'halloween' | 'winter' | 'newyear';

/** Строка расписания сезона на сегодня: отмечен ли, идёт ли и с/до какого числа. */
export interface SeasonScheduleRow {
  season: MiniappSeason;
  enabled: boolean;
  in_window: boolean;
  /** Клиент видит именно его: праздник перекрывает сезон. */
  showing: boolean;
  starts_on: string | null;
  ends_on: string | null;
}

/** Темы в лог-группе партнёра (идея #268). */
export interface LogTopicsState {
  ok: boolean;
  message: string;
  is_forum: boolean;
  can_manage_topics: boolean;
  topics_created: number;
  topics_total: number;
  topics: string[];
}

function demoLogTopics(): LogTopicsState {
  return {
    ok: false,
    message: 'Проверка недоступна в демо-режиме.',
    is_forum: false,
    can_manage_topics: false,
    topics_created: 0,
    topics_total: 0,
    topics: [],
  };
}

/** Статистика автосписаний (`/api/partner/dashboard/recurrents`). */
export interface RecurrentStats {
  active: number;
  failing: number;
  pending: number;
  /** Сколько живые автосписания приносят в месяц, в копейках валюты списания. */
  monthly_minor: number;
  by_period: Record<string, number>;
  started: number;
  charged: number;
  charged_minor: number;
  failed: number;
  cancelled: number;
  ended: number;
  currency: string;
}
